/* ============================================
   Artemis Theme — Designer Page Orchestrator
   ============================================
   Renders one panel per session entry. Each entry
   picks a logo from the page-level library and the
   print locations to apply it to. Quantities are
   entered per size. "Add all to cart" expands every
   entry into one line item per size with qty > 0.
   ============================================ */

(function () {
  'use strict';

  if (!window.DesignerSession || !window.ArtemisCustomizer) return;

  var rootEl = document.querySelector('[data-designer]');
  if (!rootEl) return;

  var uploadServiceUrl = rootEl.getAttribute('data-upload-url') || '';

  // Decoration is billed through a hidden 1¢ "decoration-fee" product: one line
  // whose quantity equals the order's total decoration cost in cents. Blank when
  // the merchant hasn't created the product — decoration then isn't charged.
  var decorationVariantId = parseInt(rootEl.getAttribute('data-decoration-variant'), 10) || 0;

  // Embroidery digitizing — when enabled, artwork placed for embroidery is sent
  // to the digitizing API, which returns a real stitch (TrueView) preview,
  // small-text / thin-stroke warnings, and DST/PES machine files. When the flag
  // is off (or the URL is blank) embroidery falls back to the flat preview.
  var embroideryUrl = (rootEl.getAttribute('data-embroidery-url') || '').replace(/\/+$/, '');
  var embroideryEnabled = rootEl.getAttribute('data-embroidery-enabled') === 'true' && !!embroideryUrl;

  // Print locations are stored in a 500-unit canvas space (CANVAS_SIZE in
  // customizer-canvas.js). The theme setting says how many real-world inches
  // that 500-unit width represents — the conversion used for size + DPI.
  var CANVAS_UNITS = 500;
  var canvasInches = parseFloat(rootEl.getAttribute('data-canvas-inches')) || 22;
  var inchesPerUnit = canvasInches / CANVAS_UNITS;

  var entriesContainer = rootEl.querySelector('[data-designer-entries]');
  var emptyState = rootEl.querySelector('[data-designer-empty]');
  var entryTemplate = document.querySelector('[data-designer-entry-template]');
  var summaryList = rootEl.querySelector('[data-summary-list]');
  var summaryEmpty = rootEl.querySelector('[data-summary-empty]');
  var summaryPieces = rootEl.querySelector('[data-summary-pieces]');
  var summarySubtotal = rootEl.querySelector('[data-summary-subtotal]');
  var summaryCheckout = rootEl.querySelector('[data-summary-checkout]');
  var summaryShipDate = rootEl.querySelector('[data-summary-ship-date]');
  var summaryStatus = rootEl.querySelector('[data-summary-status]');
  var clearAllBtn = document.querySelector('[data-designer-clear]');

  /* ---- Requested ship date ----
     A valid ship date is required before pricing or checkout. Production needs a
     5-business-day lead time, so the earliest selectable date skips weekends. */
  function addBusinessDays(date, days) {
    var d = new Date(date.getTime());
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      var dow = d.getDay();
      if (dow !== 0 && dow !== 6) added++;
    }
    return d;
  }
  function toISODate(d) {
    var m = String(d.getMonth() + 1);
    var day = String(d.getDate());
    if (m.length < 2) m = '0' + m;
    if (day.length < 2) day = '0' + day;
    return d.getFullYear() + '-' + m + '-' + day;
  }
  var minShipDate = toISODate(addBusinessDays(new Date(), 5));
  function shipDateValid() {
    return !summaryShipDate || (!!summaryShipDate.value && summaryShipDate.value >= minShipDate);
  }
  if (summaryShipDate) {
    summaryShipDate.min = minShipDate;
    summaryShipDate.addEventListener('change', function () {
      // Browsers don't block typed/out-of-range values, so clamp anything earlier
      // than the lead-time floor back to empty and surface why.
      if (summaryShipDate.value && summaryShipDate.value < minShipDate) {
        summaryShipDate.value = '';
        summaryStatus.textContent = 'Requested ship date must be at least 5 business days out.';
      }
      updateCheckoutButton();
    });
  }

  var catalog = window.APPAREL_CATALOG || [];
  var catalogByHandle = {};
  catalog.forEach(function (p) { catalogByHandle[p.handle] = p; });

  var live = {};

  /* ---- Decoration method ----
     Each garment card is decorated by one method. Which methods a print area
     allows now comes from that area's own `methods` list in the print_locations
     metafield (values 'print' / 'embroidery') — see locationAllowedForMethod.
     EMBROIDERY_AREAS is the legacy fallback for products whose metafield entries
     predate `methods` (not yet backfilled); match its slugs against the area id
     or label. Once every product carries `methods`, this list can be removed.
  */
  var EMBROIDERY_AREAS = ['left_chest', 'upper_back'];
  var DECORATION_METHODS = {
    print:      { label: 'Print',      type: 'screenprinting', apiMethod: 'default' },
    embroidery: { label: 'Embroidery', type: 'embroidery',     apiMethod: 'embroidery' },
  };

  // The "Custom Name" placement carries buyer-entered text (one name per garment),
  // not a logo. It is handled by the custom-names panel rather than the logo flow.
  // NAME_LOC is the local print_locations / canvas placement id; NAME_API_LOCATION
  // is the distinct string the pricing API keys custom-name pricing on (a DTF-only
  // placement with its own tier pricing — the API forces DTF regardless of method).
  var NAME_LOC = 'name-back';
  var NAME_API_LOCATION = 'custom_name';

  function entryMethod(entry) {
    var m = entry && entry.designs && entry.designs.decorationMethod;
    return DECORATION_METHODS[m] ? m : 'print';
  }

  // Normalise an area name/id/label to a slug so EMBROIDERY_AREAS matches
  // regardless of how the print_locations metafield spells it ("Left Chest",
  // "left-chest", "left_chest" all become "left_chest").
  function normalizeArea(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function locationAllowedForMethod(method, loc) {
    if (!loc) return false;
    // Prefer the area's own methods list from the print_locations metafield.
    // A backfilled area lists exactly the methods it supports, so allowance is
    // a direct membership test (this also lets a print-only chest area opt out
    // of embroidery, which the flat EMBROIDERY_AREAS list couldn't express).
    if (Array.isArray(loc.methods) && loc.methods.length) {
      return loc.methods.indexOf(method) >= 0;
    }
    // Legacy fallback: entry predates `methods`. Print is allowed everywhere;
    // embroidery only on the hardcoded areas.
    if (method !== 'embroidery') return true;
    return EMBROIDERY_AREAS.indexOf(normalizeArea(loc.id)) >= 0 ||
      EMBROIDERY_AREAS.indexOf(normalizeArea(loc.label)) >= 0;
  }

  // Subset of a view's locations the entry's method permits. The name placement
  // is never a logo target (it holds text), so it's excluded here so logos never
  // land on it. Custom names and a back logo are instead mutually exclusive —
  // the back holds a single decoration — enforced in setNamesEnabled/selectLogo.
  function allowedLocsFor(entry, viewLocs) {
    var method = entryMethod(entry);
    return viewLocs.filter(function (loc) {
      return loc.id !== NAME_LOC && locationAllowedForMethod(method, loc);
    });
  }

  // Does the product have at least one logo area (i.e. not the name placement)
  // that permits `method`? Drives which method buttons are offered — a garment
  // with no embroidery-capable area shouldn't let the buyer pick embroidery, and
  // an embroidery-only garment shouldn't offer print.
  function productSupportsMethod(product, method) {
    var locs = (product && product.locations) || [];
    for (var i = 0; i < locs.length; i++) {
      if (locs[i].id !== NAME_LOC && locationAllowedForMethod(method, locs[i])) {
        return true;
      }
    }
    return false;
  }

  // First supported method in the toggle's display order, so a product that
  // can't be printed (embroidery-only) defaults to embroidery instead of a
  // print method with zero allowed areas. Falls back to 'print'.
  function defaultMethodFor(product) {
    var order = Object.keys(DECORATION_METHODS);
    for (var i = 0; i < order.length; i++) {
      if (productSupportsMethod(product, order[i])) return order[i];
    }
    return 'print';
  }

  // Coerce an entry whose stored/default method the product doesn't support onto
  // a supported one, so the canvas + location list are never stranded on a method
  // with no areas. Called before the method UI renders.
  function ensureSupportedMethod(record) {
    var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);
    if (productSupportsMethod(product, entryMethod(record.entry))) return;
    var entry = record.entry;
    entry.designs = entry.designs || {};
    entry.designs.decorationMethod = defaultMethodFor(product);
    window.DesignerSession.update(entry.id, { designs: entry.designs });
  }

  /* ---- Utilities ---- */
  function handleize(s) {
    return String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function formatMoney(cents) {
    var parts = (cents / 100).toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts[0] + '.' + parts[1];
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function getExt(name) {
    var parts = String(name || '').split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  function viewLabel(view) {
    return String(view || '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('image load failed')); };
      img.src = url;
    });
  }

  function findOptionIndex(product, predicate) {
    var opts = product.options || [];
    for (var i = 0; i < opts.length; i++) {
      if (predicate(String(opts[i]).toLowerCase())) return i;
    }
    return -1;
  }

  function colorIndexOf(product) {
    return findOptionIndex(product, function (n) { return n === 'color' || n === 'colour'; });
  }

  // Canonical apparel size order (small -> big). Variants often arrive
  // alphabetized (2X-Large, 3X-Large, Large, Medium, ...), so we re-sort to
  // this scale before rendering the size matrix. Anything unrecognized falls
  // to the end, in original order.
  var SIZE_ORDER = [
    'xxs', '2xs',
    'xs', 'x-small', 'extra small', 'extra-small',
    's', 'sm', 'small',
    'm', 'md', 'medium',
    'l', 'lg', 'large',
    'xl', 'x-large', 'extra large', 'extra-large',
    'xxl', '2xl', '2x-large', '2x large',
    'xxxl', '3xl', '3x-large', '3x large',
    'xxxxl', '4xl', '4x-large', '4x large',
    'xxxxxl', '5xl', '5x-large', '5x large',
    '6xl', '6x-large', '6x large',
  ];

  // Length modifiers (Tall, Big, Long) that pair with a base size. Each pushes
  // the size into a later group so all plain sizes sort before all Tall ones,
  // while the base size still orders within the group.
  var SIZE_MODIFIERS = ['tall', 'big', 'long'];

  function sizeRank(size) {
    var key = String(size == null ? '' : size).trim().toLowerCase();
    var idx = SIZE_ORDER.indexOf(key);
    if (idx >= 0) return idx;
    // Peel off a leading/trailing length modifier and rank by the base size,
    // grouping each modifier after the plain sizes.
    for (var m = 0; m < SIZE_MODIFIERS.length; m++) {
      var mod = SIZE_MODIFIERS[m];
      var re = new RegExp('(^' + mod + '\\b|\\b' + mod + '$)', 'g');
      if (re.test(key)) {
        // Match the base separator-insensitively so "x-large"/"x large" and
        // "2x-large"/"2x large" all resolve regardless of how the size is spelled.
        var base = key.replace(re, '').replace(/[\s-]+/g, '');
        var baseRank = SIZE_ORDER.length;
        for (var b = 0; b < SIZE_ORDER.length; b++) {
          if (SIZE_ORDER[b].replace(/[\s-]+/g, '') === base) { baseRank = b; break; }
        }
        return SIZE_ORDER.length * (m + 1) + baseRank;
      }
    }
    return SIZE_ORDER.length * (SIZE_MODIFIERS.length + 2);
  }

  function sizeIndexOf(product) {
    var sizeIdx = findOptionIndex(product, function (n) { return n === 'size' || n.indexOf('size') >= 0; });
    if (sizeIdx >= 0) return sizeIdx;
    var colorIdx = colorIndexOf(product);
    var opts = product.options || [];
    for (var i = 0; i < opts.length; i++) {
      if (i !== colorIdx) return i;
    }
    return -1;
  }

  function getProductData(handle, snapshotFallback) {
    return catalogByHandle[handle] || snapshotFallback || null;
  }

  function buildMockups(product) {
    var out = {};
    // Add color/view entries from a {color,view,url} list. overwrite=true for the
    // canonical source; later sources only FILL gaps (first-write-wins).
    function add(list, overwrite) {
      (list || []).forEach(function (m) {
        if (!m.color || !m.view) return;
        if (!out[m.color]) out[m.color] = {};
        if (overwrite || !out[m.color][m.view]) out[m.color][m.view] = m.url;
      });
    }

    // customizer_mockups (canonical 500×500 mockups) — but it's populated
    // upstream from only the first 250 variants (~25 colors), so colors past the
    // cap are missing and the canvas would fall back to the FIRST color's mockup
    // (wrong garment, e.g. Russet rendering as Aqua).
    add(product.mockups, true);

    // Gallery flats fill the gaps: the product's images are the SAME normalized
    // 500×500 flats, front AND back for every color, and aren't variant-capped —
    // so this recovers both views for every color the metafield is missing.
    add(product.mockup_flats, false);

    // Final net: a color's variant image is its front flat, in case a product
    // has neither a mockup nor gallery-flat ALT for it.
    var colorIdx = colorIndexOf(product);
    if (colorIdx >= 0) {
      (product.variants || []).forEach(function (v) {
        var raw = v.options && v.options[colorIdx];
        if (!raw || !v.featuredImage) return;
        var key = handleize(raw);
        if (!out[key]) out[key] = {};
        if (!out[key].front) out[key].front = v.featuredImage;
      });
    }
    return out;
  }

  function sizesForColor(product, color) {
    var colorIdx = colorIndexOf(product);
    var sizeIdx = sizeIndexOf(product);
    var matching = (product.variants || []).filter(function (v) {
      if (colorIdx < 0) return true;
      return v.options[colorIdx] === color;
    });
    return matching.map(function (v) {
      return {
        size: sizeIdx >= 0 ? v.options[sizeIdx] : 'One Size',
        variantId: v.id,
        priceCents: v.priceCents,
        available: v.available,
        // Stock cap for the qty input (null = no limit). Shared rule in
        // apparel-stock.js — it infers tracked-ness from qty/availability/policy
        // rather than the unreliable inventory_management flag, which is what
        // previously let sizes go uncapped.
        stock: window.ApparelStock
          ? window.ApparelStock.variantStock(v)
          : ((v.inventoryManaged && v.inventoryPolicy !== 'continue')
              ? Math.max(0, parseInt(v.inventoryQuantity, 10) || 0)
              : null),
      };
    }).sort(function (a, b) {
      var ra = sizeRank(a.size);
      var rb = sizeRank(b.size);
      // Unrecognized sizes (equal rank) keep their alphabetical order.
      return ra === rb ? String(a.size).localeCompare(String(b.size)) : ra - rb;
    });
  }

  /* ---- Logo application ----
     Each product entry holds one logo PER VIEW (front/back/...), stored in
     entry.designs.logoByView. The canvas runs in 'different' mode, where art
     is keyed by print location; designer.js maps each view's logo onto that
     view's target location. With one-position-per-view enforced upstream,
     "per view" and "per active location" line up.
  */
  function findLogoByUrl(url) {
    if (!url) return null;
    var logos = window.DesignerSession.getPageLogos();
    for (var i = 0; i < logos.length; i++) {
      if (logos[i].url === url) return logos[i];
    }
    return null;
  }

  function locationsByView(product) {
    var byView = {};
    ((product && product.locations) || []).forEach(function (loc) {
      (byView[loc.view] = byView[loc.view] || []).push(loc);
    });
    return byView;
  }

  // The location a view's logo should print on, chosen only from `viewLocs`
  // (already filtered to the method's allowed areas): the remembered choice,
  // else the active location, else the first.
  function targetLocationId(record, view, viewLocs) {
    if (!viewLocs.length) return null;
    function inList(id) {
      for (var i = 0; i < viewLocs.length; i++) {
        if (viewLocs[i].id === id) return true;
      }
      return false;
    }
    var entry = record.entry;
    var remembered = entry.designs && entry.designs.locationByView && entry.designs.locationByView[view];
    if (remembered && inList(remembered)) return remembered;
    var canvas = record.controller && record.controller.canvas;
    var activeLoc = canvas && canvas.getActiveLocation();
    if (activeLoc && activeLoc.view === view && inList(activeLoc.id)) return activeLoc.id;
    return viewLocs[0].id;
  }

  function applyLogosToCanvas(record) {
    var canvas = record.controller && record.controller.canvas;
    if (!canvas) return;
    record._applying = true;
    if (canvas.mode !== 'different') canvas.setMode('different');

    var logoByView = (record.entry.designs && record.entry.designs.logoByView) || {};
    var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);
    var byView = locationsByView(product);
    // Embroidery shows a digitized stitch (TrueView) render; print shows the
    // flat artwork. Requires the digitizing feature to be configured.
    var wantTrueview = entryMethod(record.entry) === 'embroidery' && embroideryEnabled;

    Object.keys(byView).forEach(function (view) {
      var viewLocs = byView[view];
      // Only place onto areas the entry's decoration method permits.
      var targetId = targetLocationId(record, view, allowedLocsFor(record.entry, viewLocs));
      var logoId = logoByView[view];
      var logo = logoId ? window.DesignerSession.getPageLogo(logoId) : null;

      // Clear art on every location of this view except the target.
      viewLocs.forEach(function (loc) {
        if (loc.id !== targetId && canvas.perSideArt[loc.id]) canvas.clearPerSideArt(loc.id);
      });

      if (!targetId) return;
      if (logo) {
        var existing = canvas.perSideArt[targetId];
        if (existing && existing.url === logo.url) {
          // Logo already placed here. Reconcile the displayed image with the
          // decoration method: embroidery wants the stitch render, print the
          // flat artwork.
          var showingTrueview = !!(existing.digitized &&
            existing.digitized.status === 'ok' &&
            existing.previewUrl === existing.digitized.previewUrl);
          if (wantTrueview && !showingTrueview) {
            ensureDigitized(record, targetId);
          } else if (!wantTrueview && showingTrueview) {
            // Switched back to print — restore the flat artwork, keeping the
            // cached digitize result so re-selecting embroidery is instant.
            var keepDigitized = existing.digitized;
            var flatSrc = logo.previewUrl || logo.url;
            loadImage(flatSrc).then(function (img) {
              var cur = canvas.perSideArt[targetId];
              if (!cur || cur.url !== logo.url) return;
              canvas.setPerSideArt(targetId, img, logo.url, logo.key, flatSrc);
              if (keepDigitized && canvas.perSideArt[targetId]) {
                canvas.perSideArt[targetId].digitized = keepDigitized;
              }
            });
          }
          return;
        }
        var src = logo.previewUrl || logo.url;
        loadImage(src)
          .then(function (img) {
            canvas.setPerSideArt(targetId, img, logo.url, logo.key, src);
            // Embroidery: digitize the freshly-placed artwork into a stitch
            // preview. The flat image stays on screen until that resolves.
            if (wantTrueview) ensureDigitized(record, targetId);
          })
          .catch(function () { canvas.setPerSideArt(targetId, null, logo.url, logo.key, src); });
      } else if (canvas.perSideArt[targetId]) {
        canvas.clearPerSideArt(targetId);
      }
    });
    record._applying = false;
  }

  /* ---- Decoration method UI ---- */
  // Flag each location button with whether the current method permits it;
  // customizer-ui.js reads this to show/hide alongside the view filter.
  function updateLocationAllowance(record) {
    var method = entryMethod(record.entry);
    var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);
    var btns = record.el.querySelectorAll('[data-customizer-location]');
    for (var i = 0; i < btns.length; i++) {
      var loc = locationById(product, btns[i].getAttribute('data-customizer-location'));
      btns[i].setAttribute(
        'data-customizer-location-allowed',
        locationAllowedForMethod(method, loc) ? 'true' : 'false'
      );
    }
  }

  function renderMethodToggle(record) {
    var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);
    var method = entryMethod(record.entry);
    var toggle = record.el.querySelector('[data-entry-method]');
    var btns = record.el.querySelectorAll('[data-entry-method] [data-method]');
    var supportedCount = 0;
    for (var i = 0; i < btns.length; i++) {
      var m = btns[i].getAttribute('data-method');
      var supported = productSupportsMethod(product, m);
      if (supported) supportedCount++;
      // Hide (not just disable) an unavailable method so the toggle only ever
      // presents real choices for this garment.
      btns[i].hidden = !supported;
      btns[i].disabled = !supported;
      var isActive = supported && m === method;
      btns[i].classList.toggle('designer-entry__method-btn--active', isActive);
      btns[i].setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
    // With 0 or 1 method available there's nothing to choose — drop the whole row.
    if (toggle) toggle.hidden = supportedCount <= 1;
  }

  function setMethod(record, method) {
    if (!DECORATION_METHODS[method] || entryMethod(record.entry) === method) return;
    var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);
    if (!productSupportsMethod(product, method)) return;  // unavailable for this garment
    var entry = record.entry;
    entry.designs = entry.designs || {};
    entry.designs.decorationMethod = method;
    window.DesignerSession.update(entry.id, { designs: entry.designs });
    updateLocationAllowance(record);
    if (record.controller && record.controller.refresh) record.controller.refresh();
    // A method change starts a fresh digitize cycle — drop any stale
    // "preview unavailable" note from a previous embroidery attempt.
    record._embroideryUnavailable = false;
    // Re-place logos: switching to embroidery moves art onto allowed areas (or
    // clears it where a view has none).
    applyLogosToCanvas(record);
    renderMethodToggle(record);
    renderLogoPicker(record);
    renderPrintInfo(record);
    renderEmbroideryWarnings(record);
    refreshSummary();
  }

  /* ---- Cart properties ----
     Each printed location writes its own `_logo_<locId>_url`, pointing at the
     R2 original (never the Cloudinary preview) so fulfillment gets the vector.
  */
  function buildProperties(entry) {
    var props = { Customization: 'No customization', _customization_data: '' };
    var canvasState = entry.designs && entry.designs.canvasState;
    if (canvasState) props._customization_data = JSON.stringify(canvasState);

    var perSideArt = canvasState && canvasState.perSideArt;
    var locIds = perSideArt ? Object.keys(perSideArt) : [];
    if (locIds.length === 0 && !entryNamesEnabled(entry)) return props;

    var product = getProductData(entry.productHandle, entry.productSnapshot);
    var locsArr = (product && product.locations) || [];
    var locLabel = function (id) {
      for (var i = 0; i < locsArr.length; i++) {
        if (locsArr[i].id === id) return locsArr[i].label;
      }
      return id;
    };

    var summary = [];
    var embParts = [];
    // Decoration spec — the minimum needed to re-quote this line's decoration
    // from the cart (where the designer session is gone) if a product is later
    // removed: logo identity + name, method, colour count, location. Quantity is
    // read live from the cart line, never stored here. Keys are short to keep the
    // property compact. Mirrors the pricing groups in buildDecorationGroups().
    var method = entryMethod(entry);
    var methodInfo = DECORATION_METHODS[method] || {};
    var spec = [];
    locIds.forEach(function (locId) {
      var art = perSideArt[locId];
      if (!art || !art.url) return;
      props['_logo_' + locId + '_url'] = art.url;
      var logo = findLogoByUrl(art.url);
      summary.push((logo ? logo.name : 'Logo') + ' on ' + locLabel(locId));
      spec.push({
        k: logo ? String(logo.id) : art.url,
        n: logo ? logo.name : 'Logo',
        m: methodInfo.apiMethod,
        c: (logo && logo.colorCount > 0) ? logo.colorCount : 1,
        loc: locId,
      });

      // Embroidery digitize result — machine files + stitch preview ride along
      // as hidden line item properties for fulfillment; `Embroidery` is the
      // visible summary shown on the order/invoice.
      var dig = art.digitized;
      if (dig && dig.status === 'ok') {
        if (dig.dstUrl) props['_emb_' + locId + '_dst'] = dig.dstUrl;
        if (dig.pesUrl) props['_emb_' + locId + '_pes'] = dig.pesUrl;
        if (dig.previewUrl) props['_emb_' + locId + '_preview'] = dig.previewUrl;
        if (dig.stitchCount) props['_emb_' + locId + '_stitches'] = String(dig.stitchCount);
        if (dig.digitizeId) props['_emb_' + locId + '_id'] = dig.digitizeId;
        embParts.push(locLabel(locId) + ': ' +
          (dig.stitchCount ? Number(dig.stitchCount).toLocaleString() + ' stitches' : 'digitized') +
          ' (DST/PES ready)');
      }
    });
    // Custom names — priced as the API's dedicated `custom_name` (DTF-only)
    // placement. The spec's `loc` must be NAME_API_LOCATION (not NAME_LOC) so a
    // cart-side re-quote hits the same custom-name tier pricing. The actual names
    // are attached per size line in addAllToCart (each size carries its own list).
    if (entryNamesEnabled(entry) && productHasNameBack(product)) {
      summary.push('Custom names on ' + locLabel(NAME_LOC));
      spec.push({ k: 'name-back', n: 'Custom Name', m: DECORATION_METHODS.print.apiMethod, c: 1, loc: NAME_API_LOCATION });
    }

    if (summary.length) props.Customization = summary.join(', ');
    if (embParts.length) props.Embroidery = embParts.join(' · ');
    if (spec.length) props._decoration_spec = JSON.stringify(spec);

    // Decoration cost — visible properties so it shows against the product on
    // the order/invoice. Populated once a quote has been fetched.
    var deco = decorationForEntry(entry);
    if (deco) {
      props.Decoration = deco.summary;
      props['Decoration cost (per item)'] = formatMoney(deco.perUnitCents);
      props._decoration_per_unit_cents = String(deco.perUnitCents);
    }
    return props;
  }

  /* ---- Logo picker per entry ----
     The picker is scoped to the canvas's active view: it highlights the logo
     chosen for that view and writes selections into logoByView[view].
  */
  function renderLogoPicker(record) {
    var pickerEl = record.el.querySelector('[data-entry-logo-picker]');
    if (!pickerEl) return;
    var emptyEl = pickerEl.querySelector('[data-entry-logo-empty]');
    var logos = window.DesignerSession.getPageLogos();
    var canvas = record.controller && record.controller.canvas;
    var view = canvas ? canvas.activeView : null;
    var logoByView = (record.entry.designs && record.entry.designs.logoByView) || {};
    var selectedId = view ? (logoByView[view] || null) : null;

    // Title reflects which view this logo applies to (only when >1 view).
    var views = canvas ? canvas.getAvailableViews() : [];
    var titleEl = record.el.querySelector('[data-entry-logo-title]');
    if (titleEl) {
      titleEl.textContent = (views.length > 1 && view) ? 'Logo — ' + viewLabel(view) : 'Logo';
    }

    pickerEl.innerHTML = '';
    if (logos.length === 0) {
      if (emptyEl) {
        emptyEl.style.display = '';
        pickerEl.appendChild(emptyEl);
      } else {
        pickerEl.innerHTML = '<p class="designer-entry__logo-empty">Upload a logo above to apply it here.</p>';
      }
      return;
    }

    logos.forEach(function (logo) {
      var thumbSrc = logo.previewUrl || logo.url;
      var thumbHtml = '<img src="' + escapeHtml(thumbSrc) + '" alt="" crossorigin="anonymous">';
      var btn = document.createElement('button');
      btn.type = 'button';
      // Mirror the print-location buttons: the selected logo is solid/active;
      // every other logo is dimmed and labelled "(replaces)" so customers see
      // that picking it swaps off the current logo.
      var cls = 'designer-entry__logo-option';
      var isSelected = logo.id === selectedId;
      if (isSelected) {
        cls += ' designer-entry__logo-option--active';
      } else if (selectedId) {
        cls += ' designer-entry__logo-option--replaces';
      }
      btn.className = cls;
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      btn.setAttribute('data-logo-id', logo.id);
      btn.innerHTML =
        '<div class="designer-entry__logo-thumb">' + thumbHtml + '</div>' +
        '<span class="designer-entry__logo-name">' + escapeHtml(logo.name) + '</span>';
      btn.addEventListener('click', function () { selectLogo(record, logo.id); });
      pickerEl.appendChild(btn);
    });

    // Clear control — lets the customer remove the logo from this side.
    if (selectedId) {
      var clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'designer-entry__logo-clear';
      clearBtn.textContent = '✕ Remove logo' +
        (views.length > 1 && view ? ' from ' + viewLabel(view) : '');
      clearBtn.addEventListener('click', function () { clearLogo(record); });
      pickerEl.appendChild(clearBtn);
    }
  }

  function selectLogo(record, logoId) {
    var canvas = record.controller && record.controller.canvas;
    var view = canvas ? canvas.activeView : null;
    if (!view) return;
    var entry = record.entry;
    entry.designs = entry.designs || {};
    entry.designs.logoByView = entry.designs.logoByView || {};
    entry.designs.logoByView[view] = logoId;
    // The back holds a single decoration: placing a logo on the name's view
    // replaces (turns off) custom names. Names data is retained so re-enabling
    // restores it.
    var product = getProductData(entry.productHandle, entry.productSnapshot);
    var disabledNames = false;
    if (view === nameLocView(product) && entryNamesEnabled(entry)) {
      ensureCustomNames(entry).enabled = false;
      disabledNames = true;
    }
    window.DesignerSession.update(entry.id, { designs: entry.designs });
    applyLogosToCanvas(record);
    renderLogoPicker(record);
    renderPrintInfo(record);
    renderEmbroideryWarnings(record);
    if (disabledNames) {
      var nameBtn = record.el.querySelector('[data-customizer-location="' + NAME_LOC + '"]');
      if (nameBtn) nameBtn.setAttribute('data-customizer-names-enabled', 'false');
      if (record.controller && record.controller.refresh) record.controller.refresh();
      renderCustomNames(record);
    }
    refreshSummary();
  }

  // Remove the logo assigned to the active view; applyLogosToCanvas then clears
  // the art from that view's print location.
  function clearLogo(record) {
    var canvas = record.controller && record.controller.canvas;
    var view = canvas ? canvas.activeView : null;
    if (!view) return;
    var entry = record.entry;
    if (entry.designs && entry.designs.logoByView) {
      delete entry.designs.logoByView[view];
      window.DesignerSession.update(entry.id, { designs: entry.designs });
    }
    applyLogosToCanvas(record);
    renderLogoPicker(record);
    renderPrintInfo(record);
    renderEmbroideryWarnings(record);
    refreshSummary();
  }

  // Drop the logo assigned to a given view (without the re-render clearLogo does).
  // Used to enforce the back's single decoration: enabling custom names clears
  // any back logo. Returns true if a logo was actually removed.
  function clearLogoForView(record, view) {
    if (!view) return false;
    var entry = record.entry;
    var logoByView = entry.designs && entry.designs.logoByView;
    if (!logoByView || !logoByView[view]) return false;
    delete logoByView[view];
    window.DesignerSession.update(entry.id, { designs: entry.designs });
    return true;
  }

  /* ---- Print details panel (size in inches + DPI + colour count) ---- */
  function locationById(product, id) {
    var locs = (product && product.locations) || [];
    for (var i = 0; i < locs.length; i++) {
      if (locs[i].id === id) return locs[i];
    }
    return null;
  }

  function dpiTier(dpi) {
    if (dpi >= 300) return 'good';
    if (dpi >= 150) return 'ok';
    return 'low';
  }

  function colorTier(count) {
    if (count <= 6) return 'good';
    if (count <= 12) return 'ok';
    return 'high';
  }

  // Printed size of an image at a location, in inches. Mirrors the canvas's
  // contain-fit (customizer-canvas.js _renderArtAt) then converts canvas units
  // to inches via each location's real print-area size.
  function printSizeInches(loc, img) {
    var imgW = img && (img.naturalWidth || img.width);
    var imgH = img && (img.naturalHeight || img.height);
    if (!imgW || !imgH) return null;
    var scale = Math.min(loc.w / imgW, loc.h / imgH);
    var hScale = (loc.widthIn > 0 && loc.w > 0) ? (loc.widthIn / loc.w) : inchesPerUnit;
    var vScale = (loc.heightIn > 0 && loc.h > 0) ? (loc.heightIn / loc.h) : inchesPerUnit;
    return { wIn: imgW * scale * hScale, hIn: imgH * scale * vScale };
  }

  // One print-details row per placed location. Each location can carry a
  // different logo (per-view selection), so the logo is resolved per row.
  function renderPrintInfo(record) {
    var el = record.el.querySelector('[data-entry-print-info]');
    if (!el) return;

    var canvas = record.controller && record.controller.canvas;
    var perSideArt = (canvas && canvas.perSideArt) || {};
    var locIds = Object.keys(perSideArt);
    if (!locIds.length) { el.innerHTML = ''; return; }

    var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);

    var rows = locIds.map(function (id) {
      var art = perSideArt[id];
      var loc = locationById(product, id);
      var img = art && art.img;
      if (!loc || !img) return '';

      var size = printSizeInches(loc, img);
      if (!size) return '';
      var wIn = size.wIn;
      var hIn = size.hIn;

      var logo = findLogoByUrl(art.url);
      var dig = art && art.digitized;
      var digitized = !!(dig && dig.status === 'ok');

      var badges = '';
      if (digitized) {
        // Digitized for embroidery — the stitch count is the meaningful metric;
        // print size comes from the digitize result (authoritative).
        if (dig.widthMm > 0 && dig.heightMm > 0) {
          wIn = dig.widthMm / 25.4;
          hIn = dig.heightMm / 25.4;
        }
        if (dig.stitchCount > 0) {
          badges += '<span class="print-info__badge print-info__badge--stitch">' +
            Number(dig.stitchCount).toLocaleString() + ' stitches</span>';
        }
      } else {
        // Colour-count badge — shows the print colours per logo up front, so the
        // customer sees it before requesting a quote.
        if (logo && logo.colorCount > 0) {
          var c = logo.colorCount;
          badges += '<span class="print-info__badge print-info__badge--' + colorTier(c) + '">' +
            c + (c === 1 ? ' color' : ' colors') + '</span>';
        }
        // Resolution badge — vectors scale freely; rasters show their DPI.
        if (logo && logo.isVector) {
          badges += '<span class="print-info__badge print-info__badge--vector">Vector</span>';
        } else if (logo && typeof logo.width === 'number' && wIn > 0) {
          var dpi = Math.round(logo.width / wIn);
          badges += '<span class="print-info__badge print-info__badge--' + dpiTier(dpi) + '">' + dpi + ' DPI</span>';
        }
      }

      var name = logo ? logo.name : 'Logo';
      return '<li class="print-info__row">' +
        '<span class="print-info__loc">' + escapeHtml(loc.label) + ' · ' + escapeHtml(name) + '</span>' +
        '<span class="print-info__size">' + wIn.toFixed(1) + '" × ' + hIn.toFixed(1) + '"</span>' +
        badges +
        '</li>';
    }).join('');

    if (!rows) { el.innerHTML = ''; return; }
    el.innerHTML =
      '<p class="print-info__title">Print details</p>' +
      '<ul class="print-info__list">' + rows + '</ul>';
  }

  /* ---- Embroidery digitizing ----
     When an entry is decorated by embroidery, each placed logo is digitized by
     the backend: it returns a real stitch (TrueView) preview, machine files
     (DST/PES), and warnings for text/strokes too small to embroider. Results
     are cached per (logo URL + location) in entry.designs.digitizeCache so a
     reload — or toggling print/embroidery — never re-runs a paid digitize.
  */

  // Persist the live canvas state (incl. digitize results) onto the entry.
  function persistCanvasState(record) {
    var canvas = record.controller && record.controller.canvas;
    if (!canvas) return;
    var entry = record.entry;
    entry.designs = entry.designs || {};
    entry.designs.canvasState = canvas.getState();
    window.DesignerSession.update(entry.id, { designs: entry.designs });
  }

  function afterDigitizeUpdate(record) {
    persistCanvasState(record);
    renderEmbroideryWarnings(record);
    renderPrintInfo(record);
    refreshSummary();
  }

  // Attach a digitize result to a placed location and, for embroidery, swap the
  // canvas image to the TrueView stitch render. art.url stays the original R2
  // vector so fulfillment still receives the source artwork.
  function applyDigitizedResult(record, locId, result) {
    var canvas = record.controller && record.controller.canvas;
    if (!canvas) return;
    var art = canvas.perSideArt[locId];
    if (!art) return;
    art.digitized = result;
    if (result.status === 'ok') record._embroideryUnavailable = false;

    var showTrueview = result.status === 'ok' && result.previewUrl &&
      entryMethod(record.entry) === 'embroidery' && embroideryEnabled;
    if (showTrueview && art.previewUrl !== result.previewUrl) {
      var keepUrl = art.url, keepKey = art.key;
      loadImage(result.previewUrl)
        .then(function (img) {
          var cur = canvas.perSideArt[locId];
          if (!cur || cur.url !== keepUrl) return; // logo changed meanwhile
          record._applying = true;
          canvas.setPerSideArt(locId, img, keepUrl, keepKey, result.previewUrl);
          record._applying = false;
          if (canvas.perSideArt[locId]) canvas.perSideArt[locId].digitized = result;
          afterDigitizeUpdate(record);
        })
        .catch(function () { afterDigitizeUpdate(record); });
    } else {
      afterDigitizeUpdate(record);
    }
  }

  // Ensure the artwork placed at `locId` has been digitized for embroidery.
  // No-op when the feature is off, the entry isn't embroidery, or the TrueView
  // render is already showing. Uses the per-entry cache before any network call.
  function ensureDigitized(record, locId) {
    if (!embroideryEnabled) return;
    if (entryMethod(record.entry) !== 'embroidery') return;
    var canvas = record.controller && record.controller.canvas;
    if (!canvas) return;
    var art = canvas.perSideArt[locId];
    if (!art || !art.url) return;
    // Already showing the TrueView render for this art — nothing to do.
    if (art.digitized && art.digitized.status === 'ok' &&
        art.previewUrl === art.digitized.previewUrl) return;

    var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);
    var loc = locationById(product, locId);
    if (!loc) return;
    // Print size is derived from the flat artwork's natural dimensions; bail
    // until the (flat) image has loaded.
    var img = art.img;
    if (!img || !(img.naturalWidth || img.width)) return;
    var size = printSizeInches(loc, img);
    if (!size) return;

    var cacheKey = art.url + '::' + locId;
    var entry = record.entry;
    entry.designs = entry.designs || {};
    var cache = entry.designs.digitizeCache = entry.designs.digitizeCache || {};

    // Cached success — apply without another (paid) digitize call.
    var cached = cache[cacheKey];
    if (cached && cached.status === 'ok') {
      applyDigitizedResult(record, locId, cached);
      return;
    }

    record._digitizing = record._digitizing || {};
    if (record._digitizing[cacheKey]) return;
    record._digitizing[cacheKey] = true;

    var logo = findLogoByUrl(art.url);
    var payload = {
      logoUrl: art.url,
      widthMm: Math.round(size.wIn * 25.4 * 10) / 10,
      heightMm: Math.round(size.hIn * 25.4 * 10) / 10,
      fabric: 'cotton',
      colorCount: (logo && logo.colorCount > 0) ? logo.colorCount : 1,
    };

    fetch(embroideryUrl + '/embroidery/digitize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Digitize failed (' + res.status + ')');
        return res.json();
      })
      .then(function (data) {
        delete record._digitizing[cacheKey];
        var result = {
          status: 'ok',
          previewUrl: data.previewUrl || '',
          dstUrl: data.dstUrl || '',
          pesUrl: data.pesUrl || '',
          stitchCount: data.stitchCount || 0,
          widthMm: data.widthMm || payload.widthMm,
          heightMm: data.heightMm || payload.heightMm,
          digitizeId: data.digitizeId || '',
          warnings: Array.isArray(data.warnings) ? data.warnings : [],
        };
        cache[cacheKey] = result;
        window.DesignerSession.update(entry.id, { designs: entry.designs });
        var curArt = canvas.perSideArt[locId];
        if (curArt && curArt.url === payload.logoUrl) applyDigitizedResult(record, locId, result);
      })
      .catch(function () {
        // Graceful degradation — keep the flat preview, do not block checkout.
        // The error is not cached, so a reload or method re-toggle retries.
        delete record._digitizing[cacheKey];
        var curArt = canvas.perSideArt[locId];
        if (curArt && curArt.url === payload.logoUrl) {
          curArt.digitized = { status: 'error', warnings: [] };
        }
        record._embroideryUnavailable = true;
        renderEmbroideryWarnings(record);
        persistCanvasState(record);
        refreshSummary();
      });

    // Reflect the in-progress state right away (checkout gating + status).
    renderEmbroideryWarnings(record);
    refreshSummary();
  }

  // '' = no embroidery issue; otherwise a customer-facing reason it isn't ready.
  function embroideryIssue(entry) {
    if (!embroideryEnabled) return '';
    if (entryMethod(entry) !== 'embroidery') return '';
    var perSideArt = entry.designs && entry.designs.canvasState && entry.designs.canvasState.perSideArt;
    if (!perSideArt) return '';
    var pending = false;
    var blocked = false;
    Object.keys(perSideArt).forEach(function (locId) {
      var art = perSideArt[locId];
      if (!art || !art.url) return;
      var dig = art.digitized;
      if (!dig) { pending = true; return; }      // digitize not finished yet
      if (dig.status === 'error') return;        // failed — fall back, don't block
      (dig.warnings || []).forEach(function (w) {
        if (w && w.level === 'block') blocked = true;
      });
    });
    if (blocked) return 'Some artwork has text or strokes too small to embroider — replace it to continue.';
    if (pending) return 'Finishing the embroidery stitch preview…';
    return '';
  }

  function embroideryReady(entry) {
    return embroideryIssue(entry) === '';
  }

  // Inline per-entry warning rows: amber for advisory, red for blocking.
  function renderEmbroideryWarnings(record) {
    var el = record.el.querySelector('[data-entry-embroidery-warnings]');
    if (!el) return;
    if (entryMethod(record.entry) !== 'embroidery' || !embroideryEnabled) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }
    var canvas = record.controller && record.controller.canvas;
    var perSideArt = (canvas && canvas.perSideArt) || {};
    var rows = [];
    Object.keys(perSideArt).forEach(function (locId) {
      var dig = perSideArt[locId] && perSideArt[locId].digitized;
      if (!dig || !dig.warnings) return;
      dig.warnings.forEach(function (w) {
        if (!w || !w.message) return;
        var level = w.level === 'block' ? 'block' : 'warn';
        rows.push('<li class="embroidery-warning embroidery-warning--' + level + '">' +
          escapeHtml(w.message) + '</li>');
      });
    });
    if (record._embroideryUnavailable) {
      rows.push('<li class="embroidery-warning embroidery-warning--info">' +
        'Live stitch preview unavailable — our team will digitize your logo after checkout.</li>');
    }
    if (!rows.length) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }
    el.hidden = false;
    el.innerHTML = '<ul class="embroidery-warning__list">' + rows.join('') + '</ul>';
  }

  /* ---- Entry rendering ---- */
  function renderAllEntries() {
    Object.keys(live).forEach(function (id) {
      if (live[id].controller) live[id].controller.destroy();
    });
    live = {};
    entriesContainer.innerHTML = '';
    if (emptyState) entriesContainer.appendChild(emptyState);

    var entries = window.DesignerSession.list();
    updateClearButton();
    if (entries.length === 0) {
      emptyState.style.display = '';
    } else {
      emptyState.style.display = 'none';
      entries.forEach(renderEntry);
    }
    refreshSummary();
  }

  function renderEntry(entry) {
    var product = getProductData(entry.productHandle, entry.productSnapshot);
    if (!product) return;

    var fragment = entryTemplate.content.cloneNode(true);
    var entryEl = fragment.querySelector('[data-designer-entry]');
    entryEl.setAttribute('data-entry-id', entry.id);

    var imgEl = entryEl.querySelector('[data-entry-image]');
    if (imgEl) {
      imgEl.src = product.featuredImage || '';
      imgEl.alt = product.title;
    }
    entryEl.querySelector('[data-entry-title]').textContent = product.title;
    entryEl.querySelector('[data-entry-color]').textContent = entry.color ? 'Color: ' + entry.color : '';

    // View tabs
    var viewSet = {};
    (product.mockups || []).forEach(function (m) { if (m.view) viewSet[m.view] = true; });
    var views = Object.keys(viewSet);
    var viewTabsEl = entryEl.querySelector('[data-customizer-tabs]');
    if (views.length > 1) {
      views.forEach(function (view, i) {
        var tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'customizer__view-tab' + (i === 0 ? ' customizer__view-tab--active' : '');
        tab.setAttribute('data-customizer-view', view);
        tab.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
        tab.textContent = view.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        viewTabsEl.appendChild(tab);
      });
    } else {
      viewTabsEl.style.display = 'none';
    }

    // Locations
    var locGrid = entryEl.querySelector('[data-customizer-locations]');
    (product.locations || []).forEach(function (loc) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'customizer__location-btn';
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('data-customizer-location', loc.id);
      btn.setAttribute('data-customizer-location-view', loc.view);
      btn.setAttribute('aria-label', (loc.id === NAME_LOC ? 'Add custom names at ' : 'Place logo at ') + loc.label);
      btn.textContent = loc.label;
      locGrid.appendChild(btn);
    });

    if (emptyState) emptyState.style.display = 'none';
    entriesContainer.appendChild(entryEl);

    // Customizer instance
    var initialColorHandle = entry.color ? handleize(entry.color) : '';
    var hydratedCanvasState = entry.designs && entry.designs.canvasState;
    var record = { entry: entry, el: entryEl, controller: null, sizesQty: Object.assign({}, entry.sizesQty || {}) };
    live[entry.id] = record;

    var controller = window.ArtemisCustomizer.create(entryEl, {
      locations: product.locations,
      mockups: buildMockups(product),
      uploadServiceUrl: uploadServiceUrl,
      initialColor: initialColorHandle,
      initialMode: 'different',
      onChange: function (canvasState) {
        entry.designs = entry.designs || {};
        entry.designs.canvasState = canvasState;
        // Remember which location is active in the current view, so a view's
        // logo lands back on the same spot when the customer returns to it. The
        // name placement is skipped — it isn't a logo target.
        if (canvasState.view && canvasState.locationId && canvasState.locationId !== NAME_LOC) {
          entry.designs.locationByView = entry.designs.locationByView || {};
          entry.designs.locationByView[canvasState.view] = canvasState.locationId;
        }
        window.DesignerSession.update(entry.id, { designs: entry.designs });

        var rec = live[entry.id];
        if (!rec) { refreshSummary(); return; }
        // When the customer switches view or active location, re-apply so the
        // active view's logo follows onto the newly-selected location. The key
        // guard stops the setPerSideArt calls inside applyLogosToCanvas from
        // looping back here.
        var key = (canvasState.view || '') + '|' + (canvasState.locationId || '');
        var locChanged = rec._lastViewLocKey != null && rec._lastViewLocKey !== key && !rec._applying;

        // Single-decoration rule for the back: activating a back LOGO location
        // (Full Back / Upper Back) while custom names is on replaces (disables)
        // the names — the back holds one decoration. Without this both stay
        // selected and the NAME keeps drawing under the newly-picked logo spot.
        // Mirrors how applying a logo to the name's view disables names
        // (selectLogo). Gated on a real user-driven location change so init /
        // re-apply passes don't clear names. Names data is retained, so toggling
        // Custom Name back on restores it.
        if (locChanged && entryNamesEnabled(entry) &&
            canvasState.locationId && canvasState.locationId !== NAME_LOC &&
            canvasState.view === nameLocView(getProductData(entry.productHandle, entry.productSnapshot))) {
          setNamesEnabled(rec, false);
        }

        if (locChanged) {
          rec._lastViewLocKey = key;
          applyLogosToCanvas(rec);
        } else {
          rec._lastViewLocKey = key;
        }
        renderLogoPicker(rec);
        renderPrintInfo(rec);
        renderEmbroideryWarnings(rec);
        refreshSummary();
      },
    });

    if (!controller) {
      entryEl.remove();
      delete live[entry.id];
      return;
    }
    record.controller = controller;
    if (hydratedCanvasState) controller.rehydrate(hydratedCanvasState);

    // Decoration method — coerce onto a supported method first (embroidery-only
    // garments default to embroidery), then render the toggle + location allowance.
    ensureSupportedMethod(record);
    updateLocationAllowance(record);
    renderMethodToggle(record);
    var methodBtns = entryEl.querySelectorAll('[data-entry-method] [data-method]');
    for (var mi = 0; mi < methodBtns.length; mi++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          setMethod(record, btn.getAttribute('data-method'));
        });
      })(methodBtns[mi]);
    }

    // Auto-pick a logo for the starting view when nothing is chosen yet.
    // Legacy entries stored one logo as `selectedLogoId`; carry it over.
    var pageLogos = window.DesignerSession.getPageLogos();
    var legacyLogoId = entry.designs && entry.designs.selectedLogoId;
    var logoByView = (entry.designs && entry.designs.logoByView) || {};
    var startView = controller.canvas.activeView;
    if (!Object.keys(logoByView).length && startView) {
      var firstPick = legacyLogoId || (pageLogos[0] && pageLogos[0].id);
      if (firstPick) {
        entry.designs = entry.designs || {};
        entry.designs.logoByView = {};
        entry.designs.logoByView[startView] = firstPick;
        if (entry.designs.selectedLogoId) delete entry.designs.selectedLogoId;
        window.DesignerSession.update(entry.id, { designs: entry.designs });
      }
    }

    record._lastViewLocKey = (controller.canvas.activeView || '') +
      '|' + (controller.canvas.activeLocationId || '');
    applyLogosToCanvas(record);
    renderLogoPicker(record);
    renderPrintInfo(record);
    renderEmbroideryWarnings(record);

    // Size matrix
    var sizesGridEl = entryEl.querySelector('[data-entry-sizes]');
    var sizes = sizesForColor(product, entry.color);
    if (sizes.length === 0) {
      sizesGridEl.innerHTML = '<p class="designer-entry__sizes-empty">No sizes available for this color.</p>';
    } else {
      sizes.forEach(function (s) {
        var cell = document.createElement('div');
        cell.className = 'designer-entry__size';
        var hasCap = s.stock != null;
        // Clamp any restored quantity to current stock so a stale session can't
        // carry an over-inventory amount into the form.
        var initialQty = (entry.sizesQty && entry.sizesQty[s.size]) || 0;
        if (hasCap && initialQty > s.stock) {
          initialQty = s.stock;
          record.sizesQty[s.size] = initialQty;
        }
        // For stock-limited sizes, always show the cap up front (e.g. "5 max")
        // so it's clear the field won't accept more; it switches to an emphasized
        // "limit reached" state once they hit it.
        var noteText = !hasCap ? '' : (s.stock === 0 ? 'Out of stock' : s.stock + ' max');
        var atCap = hasCap && initialQty >= s.stock;
        cell.innerHTML =
          '<label class="designer-entry__size-label">' + escapeHtml(s.size) + '</label>' +
          '<input type="number" min="0" step="1"' + (hasCap ? ' max="' + s.stock + '"' : '') +
          ' value="' + initialQty + '" class="designer-entry__size-input" ' +
          'data-size="' + escapeHtml(s.size) + '" data-variant-id="' + s.variantId + '" data-price="' + s.priceCents + '"' +
          (hasCap ? ' data-stock="' + s.stock + '"' : '') + '>' +
          '<span class="designer-entry__size-note' + (atCap ? ' designer-entry__size-note--limit' : '') + '"' +
          ' data-size-note' + (hasCap ? '' : ' hidden') + '>' + escapeHtml(noteText) + '</span>';
        sizesGridEl.appendChild(cell);
      });
      sizesGridEl.addEventListener('input', function (e) {
        var input = e.target.closest('input[data-size]');
        if (!input) return;
        var size = input.getAttribute('data-size');
        var qty = Math.max(0, parseInt(input.value, 10) || 0);
        // Enforce the stock cap when the variant tracks inventory and doesn't
        // allow overselling.
        var note = input.parentNode.querySelector('[data-size-note]');
        var hasStock = input.hasAttribute('data-stock');
        if (hasStock) {
          var stock = parseInt(input.getAttribute('data-stock'), 10) || 0;
          if (qty > stock) qty = stock;
          // The cap label stays visible at all times; it just gains the
          // emphasized "limit reached" treatment once the field is maxed out.
          if (note) {
            note.textContent = stock === 0 ? 'Out of stock' : stock + ' max';
            note.classList.toggle('designer-entry__size-note--limit', qty >= stock);
          }
        }
        input.value = String(qty);
        record.sizesQty[size] = qty;
        var nextSizes = Object.assign({}, record.sizesQty);
        window.DesignerSession.update(entry.id, { sizesQty: nextSizes });
        updateEntrySubtotal(record);
        // Keep the custom-name fields in step with the new quantities.
        renderCustomNames(record);
        refreshSummary();
      });
    }
    updateEntrySubtotal(record);
    wireCustomNames(record);

    entryEl.querySelector('[data-entry-remove]').addEventListener('click', function () {
      removeEntry(entry.id);
    });

    // Zoom toggle — enlarges the canvas so the customer can clearly inspect
    // the design; the print locations + logo picker drop below while zoomed.
    var zoomBtn = entryEl.querySelector('[data-entry-zoom]');
    if (zoomBtn) {
      zoomBtn.addEventListener('click', function () {
        var zoomed = entryEl.classList.toggle('designer-entry--zoomed');
        var label = zoomed ? 'Minimize preview' : 'Enlarge preview';
        zoomBtn.setAttribute('title', label);
        zoomBtn.setAttribute('aria-label', label);
      });
    }

    entryEl.querySelector('[data-entry-duplicate]').addEventListener('click', function () {
      window.DesignerSession.add({
        productHandle: entry.productHandle,
        productId: entry.productId,
        color: entry.color,
        productSnapshot: entry.productSnapshot,
      });
      renderAllEntries();
    });
  }

  function updateEntrySubtotal(record) {
    var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);
    if (!product) return;
    var sizes = sizesForColor(product, record.entry.color);
    var byName = {};
    sizes.forEach(function (s) { byName[s.size] = s; });

    var cents = 0;
    Object.keys(record.sizesQty).forEach(function (size) {
      var qty = record.sizesQty[size] || 0;
      var info = byName[size];
      if (info) cents += info.priceCents * qty;
    });
    var el = record.el.querySelector('[data-entry-subtotal]');
    if (el) el.textContent = formatMoney(cents);
  }

  function removeEntry(entryId) {
    var rec = live[entryId];
    if (rec && rec.controller) rec.controller.destroy();
    delete live[entryId];
    window.DesignerSession.remove(entryId);
    renderAllEntries();
  }

  /* ---- Summary ---- */
  function refreshSummary() {
    var entries = window.DesignerSession.list();
    summaryList.innerHTML = '';
    var anyEntries = entries.length > 0;
    if (!anyEntries) {
      summaryList.appendChild(summaryEmpty);
      summaryEmpty.style.display = '';
    } else {
      summaryEmpty.style.display = 'none';
    }

    var totalPieces = 0;
    var totalCents = 0;
    var allHaveSizes = anyEntries;
    var allDecorated = anyEntries;
    var namesIssue = '';

    entries.forEach(function (entry) {
      var product = getProductData(entry.productHandle, entry.productSnapshot);
      if (!product) return;
      var sizes = sizesForColor(product, entry.color);
      var byName = {};
      sizes.forEach(function (s) { byName[s.size] = s; });

      var pieces = 0;
      var cents = 0;
      Object.keys(entry.sizesQty || {}).forEach(function (size) {
        var qty = entry.sizesQty[size] || 0;
        pieces += qty;
        var info = byName[size];
        if (info) cents += info.priceCents * qty;
      });
      totalPieces += pieces;
      totalCents += cents;
      if (pieces === 0) allHaveSizes = false;
      if (!entryDecorated(entry)) allDecorated = false;
      // Custom names in use but not fully filled blocks pricing/checkout.
      if (entryNamesEnabled(entry)) {
        var np = namesProgress(product, entry.color, entry.sizesQty, entryNames(entry));
        if (np.total > 0 && np.filled < np.total) {
          namesIssue = 'Enter all ' + np.total + ' custom names.';
        }
      }

      var li = document.createElement('li');
      li.className = 'designer-summary__item';
      var colorTxt = entry.color ? ' — ' + entry.color : '';
      li.innerHTML =
        '<div class="designer-summary__item-info">' +
          '<span class="designer-summary__item-title">' + escapeHtml(product.title + colorTxt) + '</span>' +
          '<span class="designer-summary__item-meta">' + pieces + ' pcs · ' + formatMoney(cents) + '</span>' +
        '</div>';
      summaryList.appendChild(li);
    });

    summaryPieces.textContent = String(totalPieces);
    summarySubtotal.textContent = formatMoney(totalCents);
    garmentTotalCents = totalCents;
    orderPieces = totalPieces;

    // Any change to the design invalidates a prior price quote.
    invalidatePricingIfStale();
    renderDecoration();
    updateCheckoutButton();
    var embIssue = '';
    entries.some(function (e) { embIssue = embroideryIssue(e); return !!embIssue; });

    if (anyEntries && !allDecorated) {
      summaryStatus.textContent = 'Apply a logo or add custom names to each product before pricing.';
    } else if (anyEntries && !allHaveSizes) {
      summaryStatus.textContent = 'Enter size quantities for each product.';
    } else if (anyEntries && namesIssue) {
      summaryStatus.textContent = namesIssue;
    } else if (anyEntries && embIssue) {
      summaryStatus.textContent = embIssue;
    } else if (anyEntries && !shipDateValid()) {
      summaryStatus.textContent = 'Choose a requested ship date (at least 5 business days out).';
    } else if (pricingState !== 'pricing') {
      summaryStatus.textContent = '';
    }
  }

  /* ---- Decoration pricing ----
     Checkout is two-staged: the customer first gets a decoration quote, then
     adds to cart. Pricing groups every placed logo across the whole order by
     (logo, location, method) and sums the garment quantity, so volume discounts
     apply to the full print run rather than a single product.

     Every group is priced in a single POST to the public /pricing/decorations
     endpoint. No sign-in required.
  */
  var pricingState = 'unpriced';   // 'unpriced' | 'pricing' | 'priced'
  var pricedSignature = '';        // design signature captured when last priced
  var decorationGroups = [];       // priced groups for the current quote
  var decorationTotalCents = 0;
  var garmentTotalCents = 0;       // garment subtotal, mirrored from refreshSummary
  var orderPieces = 0;             // total piece count, mirrored from refreshSummary

  // Public pricing endpoint — called directly (cross-origin; the API's CORS
  // policy allows the storefront origin). No sign-in required.
  var PRICING_ENDPOINT = 'https://api.awardsandapparel.com/pricing/decorations';

  // Price every decoration group in one call. Resolves to the API's `data`
  // array — one result per group, in the order the groups were sent.
  function fetchDecorationPrices(groups) {
    var payload = {
      logos: groups.map(function (g) {
        return {
          name: g.logoName,
          decoration_method: g.apiMethod,
          color_count: g.colors,
          location: g.location,
          qty: g.units,
        };
      }),
    };
    return fetch(PRICING_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Pricing request failed (' + res.status + ')');
        return res.json();
      })
      .then(function (json) { return (json && json.data) || []; });
  }

  function entryPieces(entry) {
    var total = 0;
    Object.keys(entry.sizesQty || {}).forEach(function (s) {
      total += entry.sizesQty[s] || 0;
    });
    return total;
  }

  // True once the entry has at least one logo placed on the garment.
  function entryHasLogo(entry) {
    var perSideArt = entry.designs && entry.designs.canvasState && entry.designs.canvasState.perSideArt;
    return !!(perSideArt && Object.keys(perSideArt).length);
  }

  /* ============================================================
     Custom names — buyer-entered text, one name per garment.
     Stored on the entry as:
       entry.designs.customNames = { enabled: bool, bySize: { <size>: [name,…] } }
     Each size's array length tracks that size's quantity. Names ride to the cart
     as a per-size `Custom Names` line-item property; the placement is priced
     through the decoration quote as the API's dedicated `custom_name` (DTF-only)
     placement, which carries its own tier pricing.
     ============================================================ */
  // Preset print palette for custom names (with CMYK for production). A name's
  // color is referenced by `id`; the file's color column is matched to one of
  // these (by id or label). Keep ids lowercase + hyphen-free.
  var NAME_COLORS = [
    { id: 'black',  label: 'Black',       hex: '#1A1A1A', cmyk: '0,0,0,90' },
    { id: 'white',  label: 'White',       hex: '#FFFFFF', cmyk: '0,0,0,0' },
    { id: 'navy',   label: 'Navy',        hex: '#1B2A4A', cmyk: '72,55,0,71' },
    { id: 'royal',  label: 'Royal Blue',  hex: '#1D4ED8', cmyk: '86,63,0,15' },
    { id: 'red',    label: 'Red',         hex: '#C8102E', cmyk: '0,92,77,22' },
    { id: 'maroon', label: 'Maroon',      hex: '#6F263D', cmyk: '0,79,40,56' },
    { id: 'orange', label: 'Orange',      hex: '#FF6900', cmyk: '0,59,100,0' },
    { id: 'gold',   label: 'Gold',        hex: '#FFB81C', cmyk: '0,28,89,0' },
    { id: 'green',  label: 'Green',       hex: '#00843D', cmyk: '100,0,54,48' },
    { id: 'purple', label: 'Purple',      hex: '#5F259F', cmyk: '64,90,0,0' },
    { id: 'gray',   label: 'Gray',        hex: '#8A8D8F', cmyk: '0,0,0,46' },
  ];
  var DEFAULT_NAME_COLOR = 'black';

  function nameColorById(id) {
    for (var i = 0; i < NAME_COLORS.length; i++) {
      if (NAME_COLORS[i].id === id) return NAME_COLORS[i];
    }
    return NAME_COLORS[0];
  }

  // Match a free-text color token (file column) to a palette id, by id or label.
  // Returns null when it doesn't match any preset.
  function matchColorToken(s) {
    var n = String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!n) return null;
    for (var i = 0; i < NAME_COLORS.length; i++) {
      var c = NAME_COLORS[i];
      if (c.id === n || c.label.toLowerCase().replace(/[^a-z0-9]/g, '') === n) return c.id;
    }
    return null;
  }

  // Slots are { name, color }. Coerce older/looser shapes (a bare string, or a
  // missing color) into the canonical object.
  function normalizeSlot(slot) {
    if (slot && typeof slot === 'object') {
      return { name: String(slot.name || ''), color: slot.color || DEFAULT_NAME_COLOR };
    }
    return { name: String(slot || ''), color: DEFAULT_NAME_COLOR };
  }

  function productHasNameBack(product) {
    return !!locationById(product, NAME_LOC);
  }

  // The view the name placement lives on (the "back"). Logos on this view and
  // custom names are mutually exclusive — one decoration on the back.
  function nameLocView(product) {
    var loc = locationById(product, NAME_LOC);
    return loc ? loc.view : null;
  }

  function entryNames(entry) {
    return (entry && entry.designs && entry.designs.customNames) || null;
  }

  // Names are "on" (panel shown, decoration priced) once the buyer enables them
  // — and only for products that actually expose the name placement.
  function entryNamesEnabled(entry) {
    var cn = entryNames(entry);
    if (!cn || !cn.enabled) return false;
    return productHasNameBack(getProductData(entry.productHandle, entry.productSnapshot));
  }

  function ensureCustomNames(entry) {
    entry.designs = entry.designs || {};
    if (!entry.designs.customNames) entry.designs.customNames = { enabled: false, bySize: {} };
    if (!entry.designs.customNames.bySize) entry.designs.customNames.bySize = {};
    return entry.designs.customNames;
  }

  // Decorated = a logo is placed OR custom names are enabled. Either counts as a
  // print decoration for the pricing/checkout gates.
  function entryDecorated(entry) {
    return entryHasLogo(entry) || entryNamesEnabled(entry);
  }

  // Sizes (in catalog order) that have a quantity, each with its qty. `sizesQty`
  // is passed explicitly so callers can use the live per-record copy or the
  // persisted entry copy as appropriate.
  function namesNeededList(product, color, sizesQty) {
    var out = [];
    sizesForColor(product, color).forEach(function (s) {
      var qty = (sizesQty && sizesQty[s.size]) || 0;
      if (qty > 0) out.push({ size: s.size, qty: qty });
    });
    return out;
  }

  // { filled, total, needed } — how many name slots are filled vs required.
  function namesProgress(product, color, sizesQty, cn) {
    var needed = namesNeededList(product, color, sizesQty);
    var total = 0;
    var filled = 0;
    needed.forEach(function (n) {
      total += n.qty;
      var arr = (cn && cn.bySize && cn.bySize[n.size]) || [];
      for (var i = 0; i < n.qty; i++) {
        if (arr[i] && normalizeSlot(arr[i]).name.trim()) filled++;
      }
    });
    return { filled: filled, total: total, needed: needed };
  }

  // Names are "complete" when not in use, or when every required slot is filled.
  function entryNamesComplete(entry, product) {
    if (!entryNamesEnabled(entry)) return true;
    var p = namesProgress(product, entry.color, entry.sizesQty, entryNames(entry));
    return p.filled === p.total;
  }

  // Reconcile each size's name array length with its current quantity, dropping
  // sizes no longer ordered. Returns the customNames object (mutated in place).
  function syncNameSlots(entry, product, sizesQty) {
    var cn = ensureCustomNames(entry);
    var needed = namesNeededList(product, entry.color, sizesQty);
    var keep = {};
    needed.forEach(function (n) {
      var arr = (cn.bySize[n.size] || []).slice(0, n.qty).map(normalizeSlot);
      while (arr.length < n.qty) arr.push({ name: '', color: DEFAULT_NAME_COLOR });
      cn.bySize[n.size] = arr;
      keep[n.size] = true;
    });
    Object.keys(cn.bySize).forEach(function (size) {
      if (!keep[size]) delete cn.bySize[size];
    });
    return cn;
  }

  // Toggle the custom-names panel on/off for an entry (the "Custom Name" location
  // button drives this). Names data is retained when toggled off so re-enabling
  // restores it.
  function setNamesEnabled(record, on) {
    var cn = ensureCustomNames(record.entry);
    cn.enabled = !!on;
    // The back holds a single decoration: enabling custom names replaces (clears)
    // any logo on the name's view.
    var clearedLogo = false;
    if (on) {
      var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);
      clearedLogo = clearLogoForView(record, nameLocView(product));
    } else {
      // Disabling custom names must also drop the active-location selection on
      // the name box — otherwise clicking the button "off" clears the name
      // preview text but leaves the box highlighted (and the button active), so
      // it still looks selected. Clearing it makes the toggle fully deselect.
      var nameCanvas = record.controller && record.controller.canvas;
      if (nameCanvas && nameCanvas.activeLocationId === NAME_LOC) nameCanvas.setLocation(null);
    }
    window.DesignerSession.update(record.entry.id, { designs: record.entry.designs });
    var btn = record.el.querySelector('[data-customizer-location="' + NAME_LOC + '"]');
    if (btn) btn.setAttribute('data-customizer-names-enabled', on ? 'true' : 'false');
    if (record.controller && record.controller.refresh) record.controller.refresh();
    if (clearedLogo) {
      applyLogosToCanvas(record);
      renderLogoPicker(record);
      renderPrintInfo(record);
      renderEmbroideryWarnings(record);
    }
    renderCustomNames(record);
    refreshSummary();
  }

  // Update only the "X of N" counter without rebuilding inputs (keeps focus while
  // typing).
  function updateNamesCount(record) {
    var panel = record.el.querySelector('[data-entry-names]');
    if (!panel) return;
    var countEl = panel.querySelector('[data-entry-names-count]');
    if (!countEl) return;
    var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);
    var p = namesProgress(product, record.entry.color, record.sizesQty, entryNames(record.entry));
    var done = p.total > 0 && p.filled === p.total;
    countEl.textContent = p.total ? (p.filled + ' of ' + p.total + (done ? ' ✓' : '')) : '';
    countEl.classList.toggle('designer-entry__names-count--done', done);
  }

  // The first entered name (with its color) — used as the on-garment example.
  function firstNameSlot(entry) {
    var cn = entryNames(entry);
    if (!cn || !cn.bySize) return null;
    var sizes = Object.keys(cn.bySize);
    for (var s = 0; s < sizes.length; s++) {
      var arr = cn.bySize[sizes[s]] || [];
      for (var i = 0; i < arr.length; i++) {
        var slot = normalizeSlot(arr[i]);
        if (slot.name.trim()) return slot;
      }
    }
    return null;
  }

  // Paint an example name into the name placement on the canvas: the first name
  // entered (in its color), or a placeholder once names are enabled but empty.
  function updateNamePreview(record) {
    var canvas = record.controller && record.controller.canvas;
    if (!canvas || !canvas.setNameText) return;
    if (!entryNamesEnabled(record.entry)) {
      canvas.clearNameText();
      return;
    }
    var slot = firstNameSlot(record.entry);
    var text = slot ? slot.name : 'NAME';
    var hex = nameColorById(slot ? slot.color : DEFAULT_NAME_COLOR).hex;
    canvas.setNameText(text, hex, !slot);
  }

  // Transient feedback under the names panel (e.g. after an upload). Cleared on
  // any rebuild so it never goes stale.
  function setNamesStatus(record, msg, isWarn) {
    var el = record.el.querySelector('[data-entry-names-status]');
    if (!el) return;
    el.textContent = msg || '';
    el.hidden = !msg;
    el.classList.toggle('designer-entry__names-status--warn', !!isWarn);
  }

  // Rebuild the per-size name fields from current quantities + saved names.
  function renderCustomNames(record) {
    var entry = record.entry;
    var product = getProductData(entry.productHandle, entry.productSnapshot);
    var panel = record.el.querySelector('[data-entry-names]');
    if (!panel) return;
    setNamesStatus(record, '');
    var show = productHasNameBack(product) && entryNamesEnabled(entry);
    panel.hidden = !show;
    if (!show) { updateNamePreview(record); return; }

    var cn = syncNameSlots(entry, product, record.sizesQty);
    window.DesignerSession.update(entry.id, { designs: entry.designs });

    var colorOptions = NAME_COLORS.map(function (c) {
      return '<option value="' + c.id + '">' + escapeHtml(c.label) + '</option>';
    }).join('');

    var grid = panel.querySelector('[data-entry-names-grid]');
    grid.innerHTML = '';
    namesNeededList(product, entry.color, record.sizesQty).forEach(function (n) {
      var group = document.createElement('div');
      group.className = 'designer-entry__names-group';
      group.innerHTML = '<p class="designer-entry__names-size">' +
        escapeHtml(n.size) + ' (' + n.qty + ')</p>';
      var fields = document.createElement('div');
      fields.className = 'designer-entry__names-fields';
      var arr = cn.bySize[n.size] || [];
      for (var i = 0; i < n.qty; i++) {
        var slot = normalizeSlot(arr[i]);
        var row = document.createElement('div');
        row.className = 'designer-entry__names-row';

        var input = document.createElement('input');
        input.type = 'text';
        input.className = 'designer-entry__names-input';
        input.setAttribute('data-names-size', n.size);
        input.setAttribute('data-names-index', String(i));
        input.value = slot.name;
        input.placeholder = 'Name ' + (i + 1);

        var swatch = document.createElement('span');
        swatch.className = 'designer-entry__names-swatch';
        swatch.style.background = nameColorById(slot.color).hex;

        var select = document.createElement('select');
        select.className = 'designer-entry__names-color';
        select.setAttribute('data-names-size', n.size);
        select.setAttribute('data-names-index', String(i));
        select.innerHTML = colorOptions;
        select.value = slot.color;
        select.setAttribute('aria-label', 'Print color for name ' + (i + 1));

        row.appendChild(input);
        row.appendChild(swatch);
        row.appendChild(select);
        fields.appendChild(row);
      }
      group.appendChild(fields);
      grid.appendChild(group);
    });
    updateNamesCount(record);
    updateNamePreview(record);
  }

  // Normalize parsed rows (arrays of [name, size, color] cells) into
  // { name, size, color } objects, dropping a leading "name,size,color" header.
  function normalizeNameRows(cells) {
    var rows = cells.map(function (c) {
      return { name: String(c[0] || '').trim(),
        size: String(c[1] || '').trim(),
        color: String(c[2] || '').trim() };
    }).filter(function (r) { return r.name.length; });
    if (rows.length && /^names?$/i.test(rows[0].name) &&
        (rows[0].size === '' || /^sizes?$/i.test(rows[0].size))) {
      rows.shift();
    }
    return rows;
  }

  // Parse a .csv/.txt into { name, size, color } rows (name, size, color columns).
  function parseNamesCsv(text) {
    return normalizeNameRows(String(text || '').split(/\r?\n/).map(function (line) {
      return line.split(',');
    }));
  }

  // Size tokens vary ("L", "Large", "lg"); fold them to a canonical key so a
  // file's size matches the catalog's size regardless of spelling.
  var SIZE_ALIASES = {
    XSMALL: 'XS', EXTRASMALL: 'XS',
    SMALL: 'S', MEDIUM: 'M', MED: 'M', LARGE: 'L', LG: 'L',
    XLARGE: 'XL', EXTRALARGE: 'XL',
    XXL: '2XL', XXLARGE: '2XL',
    XXXL: '3XL', XXXXL: '4XL', XXXXXL: '5XL', XXXXXXL: '6XL',
  };
  function canonSize(s) {
    var n = String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    return SIZE_ALIASES[n] || n;
  }

  // Route file rows into each needed size's name slots using the file's size
  // column. Each slot is { name, color } (color matched to the palette, default
  // when blank/unrecognized). Names with a missing/unrecognized size, or that
  // overflow a size, fall into a leftover pool that fills any still-empty slots
  // in panel order. Returns { bySize, filled, total, extra, unmatched, badColor }.
  function mapNamesBySize(needed, rows) {
    var lookup = {};        // canonical size token -> the panel's size label
    var qtyBySize = {};
    var bySize = {};
    needed.forEach(function (n) {
      lookup[canonSize(n.size)] = n.size;
      qtyBySize[n.size] = n.qty;
      bySize[n.size] = [];
    });

    var badColor = 0;
    var toSlot = function (r) {
      var color = DEFAULT_NAME_COLOR;
      if (r.color) {
        var matched = matchColorToken(r.color);
        if (matched) color = matched; else badColor++;
      }
      return { name: r.name, color: color };
    };

    var leftovers = [];
    var unmatched = 0;
    rows.forEach(function (r) {
      var slot = toSlot(r);
      var size = r.size ? lookup[canonSize(r.size)] : null;
      if (size && bySize[size].length < qtyBySize[size]) {
        bySize[size].push(slot);
      } else {
        if (r.size && !size) unmatched++;
        leftovers.push(slot);
      }
    });

    var li = 0;
    var filled = 0;
    var total = 0;
    needed.forEach(function (n) {
      var arr = bySize[n.size];
      while (arr.length < n.qty && li < leftovers.length) arr.push(leftovers[li++]);
      while (arr.length < n.qty) arr.push({ name: '', color: DEFAULT_NAME_COLOR });
      arr.length = n.qty;
      total += n.qty;
      for (var i = 0; i < n.qty; i++) if (arr[i] && arr[i].name && arr[i].name.trim()) filled++;
    });

    return { bySize: bySize, filled: filled, total: total,
      extra: Math.max(0, leftovers.length - li), unmatched: unmatched, badColor: badColor };
  }

  function distributeNamesFromFile(record, rows) {
    var entry = record.entry;
    var product = getProductData(entry.productHandle, entry.productSnapshot);
    var needed = namesNeededList(product, entry.color, record.sizesQty);
    if (!needed.length) {
      setNamesEnabled(record, true);
      setNamesStatus(record, 'Add size quantities first, then upload your names.', true);
      return;
    }
    var result = mapNamesBySize(needed, rows);
    var cn = ensureCustomNames(entry);
    cn.enabled = true;
    cn.bySize = result.bySize;
    window.DesignerSession.update(entry.id, { designs: entry.designs });

    var btn = record.el.querySelector('[data-customizer-location="' + NAME_LOC + '"]');
    if (btn) btn.setAttribute('data-customizer-names-enabled', 'true');
    if (record.controller && record.controller.refresh) record.controller.refresh();
    renderCustomNames(record);

    var notes = [];
    if (result.unmatched) notes.push(result.unmatched + ' had an unrecognized size (placed in order)');
    if (result.badColor) notes.push(result.badColor + ' had an unrecognized color (set to default)');
    if (result.extra) notes.push(result.extra + ' extra name' + (result.extra === 1 ? '' : 's') + ' ignored');
    setNamesStatus(record,
      'Loaded ' + result.filled + ' of ' + result.total + ' names' +
        (notes.length ? ' — ' + notes.join('; ') + '.' : '.'),
      result.filled < result.total || result.unmatched > 0 || result.extra > 0 || result.badColor > 0);
    refreshSummary();
  }

  // SheetJS (xlsx) is loaded on demand the first time a spreadsheet is uploaded.
  var XLSX_SRC = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
  var xlsxPromise = null;
  function loadXlsx() {
    if (window.XLSX) return Promise.resolve(window.XLSX);
    if (xlsxPromise) return xlsxPromise;
    xlsxPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = XLSX_SRC;
      s.onload = function () { resolve(window.XLSX); };
      s.onerror = function () { reject(new Error('Could not load spreadsheet reader')); };
      document.head.appendChild(s);
    });
    return xlsxPromise;
  }

  function importNamesFile(record, file) {
    var isXlsx = /\.(xlsx|xls)$/i.test(file.name) ||
      /sheet|excel/i.test(file.type || '');
    if (isXlsx) {
      loadXlsx().then(function (XLSX) {
        var reader = new FileReader();
        reader.onload = function () {
          try {
            var wb = XLSX.read(reader.result, { type: 'array' });
            var sheet = wb.Sheets[wb.SheetNames[0]];
            var cells = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
            distributeNamesFromFile(record, normalizeNameRows(cells));
          } catch (err) {
            setNamesStatus(record, 'Could not read that spreadsheet — try a .csv.', true);
          }
        };
        reader.readAsArrayBuffer(file);
      }).catch(function () {
        setNamesStatus(record, 'Could not load the spreadsheet reader — try a .csv.', true);
      });
      return;
    }
    var r = new FileReader();
    r.onload = function () { distributeNamesFromFile(record, parseNamesCsv(r.result)); };
    r.readAsText(file);
  }

  // Build + download a name,size,color CSV pre-filled with one row per garment
  // (size column populated from current quantities), so the buyer just adds names.
  function downloadNamesTemplate(record) {
    var entry = record.entry;
    var product = getProductData(entry.productHandle, entry.productSnapshot);
    var needed = namesNeededList(product, entry.color, record.sizesQty);
    var lines = ['name,size,color'];
    if (!needed.length) {
      lines.push(',,');           // a blank example row when no sizes chosen yet
    } else {
      needed.forEach(function (n) {
        for (var i = 0; i < n.qty; i++) lines.push(',' + n.size + ',');
      });
    }
    var blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = 'custom-names-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  }

  // Wire the custom-names panel for one entry: the "Custom Name" location toggle,
  // the field-input delegation, and the upload control. No-op when the product
  // has no name placement.
  function wireCustomNames(record) {
    var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);
    if (!productHasNameBack(product)) return;

    var nameBtn = record.el.querySelector('[data-customizer-location="' + NAME_LOC + '"]');
    if (nameBtn) {
      nameBtn.setAttribute('data-customizer-names-enabled', entryNamesEnabled(record.entry) ? 'true' : 'false');
      nameBtn.addEventListener('click', function () {
        setNamesEnabled(record, !entryNamesEnabled(record.entry));
      });
    }

    // One delegated handler covers the name text fields and the color selects.
    var grid = record.el.querySelector('[data-entry-names-grid]');
    if (grid) {
      var onFieldChange = function (e) {
        var el = e.target;
        var size = el.getAttribute('data-names-size');
        if (size == null) return;
        var idx = parseInt(el.getAttribute('data-names-index'), 10) || 0;
        var cn = ensureCustomNames(record.entry);
        cn.bySize[size] = (cn.bySize[size] || []).map(normalizeSlot);
        while (cn.bySize[size].length <= idx) cn.bySize[size].push({ name: '', color: DEFAULT_NAME_COLOR });
        var slot = cn.bySize[size][idx];
        if (el.matches('input[data-names-size]')) {
          slot.name = el.value;
        } else if (el.matches('select[data-names-color]') || el.matches('select.designer-entry__names-color')) {
          slot.color = el.value;
          var sw = el.parentNode.querySelector('.designer-entry__names-swatch');
          if (sw) sw.style.background = nameColorById(slot.color).hex;
        } else {
          return;
        }
        window.DesignerSession.update(record.entry.id, { designs: record.entry.designs });
        updateNamesCount(record);
        updateNamePreview(record);
        refreshSummary();
      };
      grid.addEventListener('input', onFieldChange);
      grid.addEventListener('change', onFieldChange);
    }

    var uploadBtn = record.el.querySelector('[data-entry-names-upload]');
    var fileInput = record.el.querySelector('[data-entry-names-file]');
    if (uploadBtn && fileInput) {
      uploadBtn.addEventListener('click', function () { fileInput.click(); });
      fileInput.addEventListener('change', function () {
        var f = fileInput.files && fileInput.files[0];
        if (f) importNamesFile(record, f);
        fileInput.value = '';
      });
    }

    var templateBtn = record.el.querySelector('[data-entry-names-template]');
    if (templateBtn) {
      templateBtn.addEventListener('click', function () { downloadNamesTemplate(record); });
    }

    renderCustomNames(record);
  }

  // Stable pricing-group key for an entry's custom names. Custom names are a
  // single decoration shared across the whole order, so one key serves it.
  var NAMES_GROUP_KEY = 'name-back::custom-name::print';

  // Group placed art across every entry by (logo, location, method), summing
  // units. Method is part of the key — embroidery and print price separately.
  // Custom names contribute a single print group on the name placement.
  function buildDecorationGroups() {
    var groups = {};
    window.DesignerSession.list().forEach(function (entry) {
      var pieces = entryPieces(entry);
      if (pieces <= 0) return;
      var product = getProductData(entry.productHandle, entry.productSnapshot);
      var perSideArt = entry.designs && entry.designs.canvasState && entry.designs.canvasState.perSideArt;
      if (perSideArt) {
        var method = entryMethod(entry);
        var methodInfo = DECORATION_METHODS[method];
        Object.keys(perSideArt).forEach(function (locId) {
          var art = perSideArt[locId];
          if (!art || !art.url) return;
          var logo = findLogoByUrl(art.url);
          var key = (logo ? logo.id : art.url) + '::' + locId + '::' + method;
          if (!groups[key]) {
            var loc = locationById(product, locId);
            groups[key] = {
              key: key,
              logoName: logo ? logo.name : 'Logo',
              location: locId,
              locationLabel: loc ? loc.label : locId,
              colors: (logo && logo.colorCount > 0) ? logo.colorCount : 1,
              method: method,
              methodLabel: methodInfo.label,
              type: methodInfo.type,
              apiMethod: methodInfo.apiMethod,
              units: 0,
            };
          }
          groups[key].units += pieces;
        });
      }

      // Custom names — priced by the API as its dedicated `custom_name`
      // (DTF-only) placement. We send NAME_API_LOCATION, not NAME_LOC: the
      // backend forces DTF and applies the custom-name tier pricing.
      if (entryNamesEnabled(entry) && productHasNameBack(product)) {
        var printInfo = DECORATION_METHODS.print;
        if (!groups[NAMES_GROUP_KEY]) {
          var nbLoc = locationById(product, NAME_LOC);
          groups[NAMES_GROUP_KEY] = {
            key: NAMES_GROUP_KEY,
            logoName: 'Custom Name',
            location: NAME_API_LOCATION,
            locationLabel: nbLoc ? nbLoc.label : 'Custom Name',
            colors: 1,
            method: 'print',
            methodLabel: 'Direct to Film',
            type: printInfo.type,
            // Backend forces DTF for custom_name regardless of the method sent.
            apiMethod: printInfo.apiMethod,
            units: 0,
          };
        }
        groups[NAMES_GROUP_KEY].units += pieces;
      }
    });
    return Object.keys(groups).map(function (k) { return groups[k]; });
  }

  // Signature of every price-relevant input — placements + quantities. A
  // mismatch against `pricedSignature` means the quote is stale.
  function designSignature() {
    return JSON.stringify(window.DesignerSession.list().map(function (e) {
      var perSideArt = e.designs && e.designs.canvasState && e.designs.canvasState.perSideArt;
      var art = {};
      var dig = {};
      if (perSideArt) {
        Object.keys(perSideArt).forEach(function (locId) {
          art[locId] = perSideArt[locId].url;
          // A re-digitize (new logo or print size) yields a new digitizeId,
          // which invalidates a prior quote.
          var d = perSideArt[locId].digitized;
          if (d) dig[locId] = d.digitizeId || d.status || '1';
        });
      }
      // Method is included — switching it changes the price even when a logo
      // stays on the same (dual-allowed) location. `names` flips the quote when
      // custom names are toggled on/off (the names' text doesn't affect price,
      // only their presence and the garment count, which `qty` already carries).
      return { id: e.id, art: art, dig: dig, qty: e.sizesQty || {}, method: entryMethod(e),
        names: entryNamesEnabled(e) };
    }));
  }

  function invalidatePricingIfStale() {
    if (pricingState === 'priced' && designSignature() !== pricedSignature) {
      pricingState = 'unpriced';
      decorationGroups = [];
      decorationTotalCents = 0;
    }
  }

  // Per-garment decoration cost for one entry, summed over its placements from
  // the order-wide priced groups. Null until a quote exists.
  function decorationForEntry(entry) {
    if (pricingState !== 'priced' || !decorationGroups.length) return null;
    var byKey = {};
    decorationGroups.forEach(function (g) { byKey[g.key] = g; });

    var method = entryMethod(entry);
    var perUnitCents = 0;
    var parts = [];
    var perSideArt = entry.designs && entry.designs.canvasState && entry.designs.canvasState.perSideArt;
    if (perSideArt) {
      Object.keys(perSideArt).forEach(function (locId) {
        var art = perSideArt[locId];
        if (!art || !art.url) return;
        var logo = findLogoByUrl(art.url);
        var g = byKey[(logo ? logo.id : art.url) + '::' + locId + '::' + method];
        if (!g) return;
        perUnitCents += g.perUnitCents;
        parts.push(g.locationLabel + ' ' + formatMoney(g.perUnitCents) + '/ea');
      });
    }
    if (entryNamesEnabled(entry)) {
      var ng = byKey[NAMES_GROUP_KEY];
      if (ng) {
        perUnitCents += ng.perUnitCents;
        parts.push(ng.locationLabel + ' ' + formatMoney(ng.perUnitCents) + '/ea');
      }
    }
    if (!parts.length) return null;
    return { perUnitCents: perUnitCents, summary: parts.join(' · ') };
  }

  function renderDecoration() {
    var el = rootEl.querySelector('[data-summary-decoration]');
    if (!el) return;
    if (pricingState !== 'priced' || !decorationGroups.length) {
      el.innerHTML = '';
      return;
    }
    var rows = decorationGroups.map(function (g) {
      var colorTxt = g.colors + (g.colors === 1 ? ' color' : ' colors');
      return '<div class="designer-summary__decoration-row">' +
        '<span class="designer-summary__decoration-label">' +
          escapeHtml(g.logoName + ' · ' + g.locationLabel) +
          '<small>' + escapeHtml(g.methodLabel) + ' · ' + g.units + ' pcs × ' +
            formatMoney(g.perUnitCents) + ' · ' + colorTxt + '</small>' +
        '</span>' +
        '<span>' + formatMoney(g.totalCents) + '</span>' +
        '</div>';
    }).join('');
    var grandTotalCents = garmentTotalCents + decorationTotalCents;
    el.innerHTML =
      '<p class="designer-summary__decoration-title">Decoration</p>' +
      rows +
      '<div class="designer-summary__row">' +
        '<span>Decoration total</span><span>' + formatMoney(decorationTotalCents) + '</span>' +
      '</div>' +
      '<div class="designer-summary__row designer-summary__row--total">' +
        '<span>Total</span><span>' + formatMoney(grandTotalCents) + '</span>' +
      '</div>' +
      (orderPieces > 0
        ? '<p class="designer-summary__per-unit">' +
            formatMoney(Math.round(grandTotalCents / orderPieces)) + ' per piece</p>'
        : '');
  }

  function updateCheckoutButton() {
    var entries = window.DesignerSession.list();
    // Gate pricing/checkout until every product has a decoration (a logo or
    // custom names) and sizes, any embroidery artwork has digitized cleanly (no
    // blocking warnings), and any custom names are fully filled in.
    var ready = entries.length > 0 && shipDateValid() && entries.every(function (e) {
      var product = getProductData(e.productHandle, e.productSnapshot);
      return entryPieces(e) > 0 && entryDecorated(e) && embroideryReady(e) &&
        entryNamesComplete(e, product);
    });
    if (pricingState === 'pricing') {
      summaryCheckout.disabled = true;
      summaryCheckout.textContent = 'Calculating…';
    } else if (pricingState === 'priced') {
      summaryCheckout.disabled = !ready;
      summaryCheckout.textContent = 'Add all to cart';
    } else {
      summaryCheckout.disabled = !ready;
      summaryCheckout.textContent = 'Get decoration costs';
    }
    // Quote + Proof download is available only once a quote exists.
    var qBtn = rootEl.querySelector('[data-summary-quote]');
    if (qBtn) qBtn.hidden = (pricingState !== 'priced');
  }

  function getDecorationCosts() {
    var groups = buildDecorationGroups();
    if (!groups.length) {
      summaryStatus.textContent = 'Add a logo and size quantities first.';
      return;
    }
    // Hold pricing until every embroidery design has digitized cleanly.
    var embIssue = '';
    window.DesignerSession.list().some(function (e) {
      embIssue = embroideryIssue(e);
      return !!embIssue;
    });
    if (embIssue) {
      summaryStatus.textContent = embIssue;
      return;
    }
    pricingState = 'pricing';
    updateCheckoutButton();
    summaryStatus.textContent = 'Calculating decoration costs…';

    fetchDecorationPrices(groups)
      .then(function (data) {
        if (data.length !== groups.length) {
          throw new Error('Could not get pricing — please try again.');
        }
        groups.forEach(function (g, i) {
          // Per-unit decoration charge = sum of the line items' prices. The
          // product_multiplier is a garment markup, not part of this charge.
          var lineItems = (data[i] && data[i].line_items) || [];
          var perUnit = lineItems.reduce(function (sum, li) {
            return sum + (li.price || 0);
          }, 0);
          g.perUnitCents = Math.round(perUnit * 100);
          g.totalCents = g.perUnitCents * g.units;
        });
        decorationGroups = groups;
        decorationTotalCents = groups.reduce(function (sum, g) { return sum + g.totalCents; }, 0);
        pricedSignature = designSignature();
        pricingState = 'priced';
        summaryStatus.textContent = '';
        renderDecoration();
        updateCheckoutButton();
      })
      .catch(function (err) {
        pricingState = 'unpriced';
        summaryStatus.textContent = err.message || 'Could not get pricing — please try again.';
        updateCheckoutButton();
      });
  }

  function addAllToCart() {
    var entries = window.DesignerSession.list();
    if (entries.length === 0) return;

    // Requested ship date is required; bail (and prompt) if it's missing/invalid.
    if (!shipDateValid()) {
      summaryStatus.textContent = 'Choose a requested ship date (at least 5 business days out).';
      return;
    }
    var shipDate = summaryShipDate ? summaryShipDate.value : '';

    var lineItems = [];
    entries.forEach(function (entry) {
      var product = getProductData(entry.productHandle, entry.productSnapshot);
      if (!product) return;
      var sizes = sizesForColor(product, entry.color);
      var byName = {};
      sizes.forEach(function (s) { byName[s.size] = s; });
      var props = buildProperties(entry);
      if (shipDate) props['Requested Ship Date'] = shipDate;
      var cn = (entryNamesEnabled(entry) && productHasNameBack(product)) ? entryNames(entry) : null;

      Object.keys(entry.sizesQty || {}).forEach(function (size) {
        var qty = entry.sizesQty[size] || 0;
        if (qty <= 0) return;
        var info = byName[size];
        if (!info) return;
        var lineProps = props;
        // Each size line carries its own ordered list of names + colors (one per
        // garment): a readable `Custom Names` for the order/invoice plus a hidden
        // `_custom_names` JSON (name + color label + CMYK) for production.
        if (cn) {
          var arr = (cn.bySize && cn.bySize[size]) || [];
          var visible = [];
          var data = [];
          for (var i = 0; i < qty; i++) {
            var slot = normalizeSlot(arr[i]);
            var col = nameColorById(slot.color);
            visible.push(slot.name.trim() + ' (' + col.label + ')');
            data.push({ n: slot.name.trim(), color: col.label, cmyk: col.cmyk });
          }
          lineProps = Object.assign({}, props);
          lineProps['Custom Names'] = visible.join(', ');
          lineProps['_custom_names'] = JSON.stringify(data);
        }
        lineItems.push({ id: info.variantId, quantity: qty, properties: lineProps });
      });
    });

    if (lineItems.length === 0) return;

    // Decoration fee line — the 1¢ product carried `decorationTotalCents` times,
    // so its line price equals the order's decoration cost. Added last so it sits
    // beneath the garments in the cart. Skipped when there's nothing to charge or
    // the merchant hasn't wired up the fee product.
    if (decorationVariantId && decorationTotalCents > 0) {
      var decoSummary = decorationGroups
        .map(function (g) { return g.logoName + ' on ' + g.locationLabel; })
        .join(', ');
      lineItems.push({
        id: decorationVariantId,
        quantity: decorationTotalCents,
        properties: (function () {
          var p = {
            _decoration_fee: 'true',
            Decoration: decoSummary || 'Decoration & printing',
            _decoration_total: formatMoney(decorationTotalCents),
          };
          if (shipDate) p['Requested Ship Date'] = shipDate;
          return p;
        })(),
      });
    }

    summaryCheckout.disabled = true;
    summaryCheckout.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lineItems }),
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || 'Add to cart failed'); });
        return res.json();
      })
      .then(function () {
        window.DesignerSession.clear();
        window.location.href = '/cart';
      })
      .catch(function (err) {
        summaryStatus.textContent = err.message || 'Could not add items — please try again.';
        pricingState = 'priced';
        updateCheckoutButton();
      });
  }

  /* ---- Checkout button — two-stage: price, then add to cart ---- */
  summaryCheckout.addEventListener('click', function () {
    if (pricingState === 'priced') {
      addAllToCart();
    } else if (pricingState === 'unpriced') {
      getDecorationCosts();
    }
  });

  /* ---- Quote + Proof PDF ----
     One PDF combining the price quote and a visual proof of each garment.
     jsPDF is loaded on demand from a CDN the first time it's needed.
  */
  var JSPDF_SRC = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.2/dist/jspdf.umd.min.js';
  var jspdfLoadPromise = null;
  var quoteBtn = rootEl.querySelector('[data-summary-quote]');

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('Failed to load ' + src)); };
      document.head.appendChild(s);
    });
  }

  function ensureJsPDF() {
    if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
    if (!jspdfLoadPromise) {
      jspdfLoadPromise = loadScript(JSPDF_SRC).then(function () {
        if (!window.jspdf || !window.jspdf.jsPDF) throw new Error('jsPDF unavailable after load');
        return window.jspdf.jsPDF;
      });
    }
    return jspdfLoadPromise;
  }

  // Wait (briefly) for the canvas's garment mockup to finish loading.
  function waitForGarment(canvas, done) {
    var tries = 0;
    (function poll() {
      if (canvas.garmentImage || tries > 40) { done(); return; }
      tries++;
      setTimeout(poll, 50);
    })();
  }

  // Capture a proof image of every view that carries art on this entry, by
  // flipping the live canvas through each view. Restores the original view.
  function captureEntryProofs(record) {
    var canvas = record.controller && record.controller.canvas;
    if (!canvas) return Promise.resolve([]);
    var perSide = canvas.perSideArt || {};
    var views = [];
    canvas.locations.forEach(function (loc) {
      if (perSide[loc.id] && views.indexOf(loc.view) < 0) views.push(loc.view);
    });
    if (!views.length) return Promise.resolve([]);

    var originalView = canvas.activeView;
    var proofs = [];
    return views.reduce(function (chain, view) {
      return chain.then(function () {
        return new Promise(function (resolve) {
          canvas.setView(view);
          waitForGarment(canvas, function () {
            requestAnimationFrame(function () {
              try {
                proofs.push({ label: viewLabel(view), dataUrl: canvas.toPreviewDataURL() });
              } catch (e) { /* tainted canvas — skip this proof */ }
              resolve();
            });
          });
        });
      });
    }, Promise.resolve()).then(function () {
      if (originalView && originalView !== canvas.activeView) canvas.setView(originalView);
      return proofs;
    });
  }

  function entryGarmentCents(entry) {
    var product = getProductData(entry.productHandle, entry.productSnapshot);
    if (!product) return 0;
    var byName = {};
    sizesForColor(product, entry.color).forEach(function (s) { byName[s.size] = s; });
    var cents = 0;
    Object.keys(entry.sizesQty || {}).forEach(function (size) {
      var info = byName[size];
      if (info) cents += info.priceCents * (entry.sizesQty[size] || 0);
    });
    return cents;
  }

  function sizeBreakdown(entry) {
    var parts = [];
    Object.keys(entry.sizesQty || {}).forEach(function (size) {
      var q = entry.sizesQty[size] || 0;
      if (q > 0) parts.push(size + ' × ' + q);
    });
    return parts.join('   ');
  }

  // Placed-art rows for one entry: view, location, logo, colours, print size.
  function placementsForEntry(record) {
    var canvas = record.controller && record.controller.canvas;
    if (!canvas) return [];
    var perSide = canvas.perSideArt || {};
    var product = getProductData(record.entry.productHandle, record.entry.productSnapshot);
    var out = [];
    Object.keys(perSide).forEach(function (locId) {
      var art = perSide[locId];
      var loc = locationById(product, locId);
      if (!loc) return;
      var logo = findLogoByUrl(art.url);
      var sizeStr = '';
      if (art.img) {
        var sz = printSizeInches(loc, art.img);
        if (sz) sizeStr = sz.wIn.toFixed(1) + '" × ' + sz.hIn.toFixed(1) + '"';
      }
      out.push({
        viewLabel: viewLabel(loc.view),
        locationLabel: loc.label,
        logoName: logo ? logo.name : 'Logo',
        colors: (logo && logo.colorCount > 0) ? logo.colorCount : 1,
        sizeStr: sizeStr,
      });
    });
    return out;
  }

  function downloadQuotePdf() {
    if (quoteBtn) { quoteBtn.disabled = true; quoteBtn.textContent = 'Preparing…'; }
    summaryStatus.textContent = '';

    var entries = window.DesignerSession.list();
    Promise.all([
      ensureJsPDF(),
      entries.reduce(function (chain, entry) {
        return chain.then(function (acc) {
          var rec = live[entry.id];
          var proofsPromise = rec ? captureEntryProofs(rec) : Promise.resolve([]);
          return proofsPromise.then(function (proofs) {
            acc.push({
              product: getProductData(entry.productHandle, entry.productSnapshot),
              color: entry.color,
              methodLabel: DECORATION_METHODS[entryMethod(entry)].label,
              proofs: proofs,
              placements: rec ? placementsForEntry(rec) : [],
              pieces: entryPieces(entry),
              sizeStr: sizeBreakdown(entry),
              garmentCents: entryGarmentCents(entry),
              deco: decorationForEntry(entry),
            });
            return acc;
          });
        });
      }, Promise.resolve([])),
    ])
      .then(function (res) { renderQuotePdf(res[0], res[1]); })
      .catch(function (err) {
        summaryStatus.textContent = err.message || 'Could not build the PDF.';
      })
      .then(function () {
        if (quoteBtn) { quoteBtn.disabled = false; quoteBtn.textContent = 'Download Quote + Proof'; }
      });
  }

  function renderQuotePdf(JsPDF, items) {
    var doc = new JsPDF({ unit: 'mm', format: 'letter' });
    var PW = doc.internal.pageSize.getWidth();
    var PH = doc.internal.pageSize.getHeight();
    var M = 14;
    var CW = PW - M * 2;
    var y = M;

    var DARK = [38, 38, 38];
    var GREY = [110, 108, 117];
    var GOLD = [212, 168, 83];

    function setColor(rgb) { doc.setTextColor(rgb[0], rgb[1], rgb[2]); }
    function need(h) { if (y + h > PH - M) { doc.addPage(); y = M; } }
    function rule(color, weight) {
      doc.setDrawColor(color[0], color[1], color[2]);
      doc.setLineWidth(weight);
      doc.line(M, y, PW - M, y);
    }

    /* ---- Header ---- */
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    setColor(DARK);
    doc.text('Artemis Awards & Apparel', M, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    setColor(GREY);
    doc.text('Quote & Proof', M, y + 11);
    doc.text(new Date().toLocaleDateString(), PW - M, y + 5, { align: 'right' });
    doc.text('440.255.2193   sales@awardsandapparel.com', PW - M, y + 11, { align: 'right' });
    y += 16;
    rule(GOLD, 0.6);
    y += 8;

    /* ---- Entries ---- */
    items.forEach(function (item) {
      if (!item.product) return;
      var proofW = 44;
      var blockH = 14 + proofW + 8 + item.placements.length * 5 + 16;
      need(blockH);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      setColor(DARK);
      var title = item.product.title + (item.color ? '  —  ' + item.color : '');
      doc.text(title, M, y + 4);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      setColor(GREY);
      doc.text(item.methodLabel, PW - M, y + 4, { align: 'right' });
      y += 10;

      // Proof images, side by side.
      var px = M;
      item.proofs.forEach(function (proof) {
        try { doc.addImage(proof.dataUrl, 'JPEG', px, y, proofW, proofW); } catch (e) { /* skip */ }
        doc.setDrawColor(225, 222, 216);
        doc.setLineWidth(0.2);
        doc.rect(px, y, proofW, proofW);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        setColor(GREY);
        doc.text(proof.label, px + proofW / 2, y + proofW + 4, { align: 'center' });
        px += proofW + 6;
      });

      // Details column, to the right of the proofs.
      var dx = M + item.proofs.length * (proofW + 6) + 4;
      if (!item.proofs.length) dx = M;
      var dy = y + 4;
      doc.setFontSize(9);
      item.placements.forEach(function (p) {
        setColor(DARK);
        doc.setFont('helvetica', 'bold');
        doc.text(p.viewLabel + ' · ' + p.locationLabel, dx, dy);
        dy += 4.5;
        setColor(GREY);
        doc.setFont('helvetica', 'normal');
        var meta = p.logoName + '   ' + p.colors + (p.colors === 1 ? ' color' : ' colors');
        if (p.sizeStr) meta += '   ' + p.sizeStr;
        doc.text(meta, dx, dy);
        dy += 6;
      });

      // Sizes + costs sit below whichever column is taller.
      y = Math.max(y + proofW + 8, dy + 2);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      setColor(DARK);
      doc.text('Sizes:  ' + (item.sizeStr || '—') + '    (' + item.pieces + ' pcs)', M, y);
      y += 5;
      var decoCents = item.deco ? item.deco.perUnitCents * item.pieces : 0;
      doc.text(
        'Garments ' + formatMoney(item.garmentCents) +
        '     Decoration ' + formatMoney(decoCents),
        M, y
      );
      y += 6;
      rule([225, 222, 216], 0.3);
      y += 7;
    });

    /* ---- Totals ---- */
    need(36);
    var decoTotal = 0;
    var garmentTotal = 0;
    var pieces = 0;
    items.forEach(function (item) {
      garmentTotal += item.garmentCents;
      pieces += item.pieces;
      if (item.deco) decoTotal += item.deco.perUnitCents * item.pieces;
    });
    var grand = garmentTotal + decoTotal;

    function totalRow(label, value, bold, size) {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(size || 10);
      setColor(bold ? DARK : GREY);
      doc.text(label, M, y);
      doc.text(value, PW - M, y, { align: 'right' });
      y += (size || 10) * 0.55;
    }
    totalRow('Pieces', String(pieces), false);
    totalRow('Garment costs', formatMoney(garmentTotal), false);
    totalRow('Decoration total', formatMoney(decoTotal), false);
    y += 1;
    rule(GOLD, 0.5);
    y += 6;
    totalRow('ORDER TOTAL', formatMoney(grand), true, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    setColor(GREY);
    if (pieces > 0) {
      doc.text(formatMoney(Math.round(grand / pieces)) + ' per piece', PW - M, y, { align: 'right' });
    }
    y += 8;
    doc.text(
      'This quote is an estimate. Taxes & shipping calculated at checkout; final pricing confirmed on order.',
      M, y
    );

    doc.save('Artemis-Quote-Proof.pdf');
  }

  if (quoteBtn) {
    quoteBtn.addEventListener('click', downloadQuotePdf);
  }

  /* ---- Page logos changed: drop deleted logos from per-view selections ---- */
  window.DesignerSession.on('pageLogos', function (logos) {
    var validIds = {};
    logos.forEach(function (l) { validIds[l.id] = true; });

    Object.keys(live).forEach(function (id) {
      var rec = live[id];
      if (!rec) return;
      var entry = rec.entry;
      var logoByView = (entry.designs && entry.designs.logoByView) || {};
      var changed = false;
      Object.keys(logoByView).forEach(function (view) {
        if (!validIds[logoByView[view]]) {
          delete logoByView[view];
          changed = true;
        }
      });
      if (changed) {
        entry.designs.logoByView = logoByView;
        window.DesignerSession.update(entry.id, { designs: entry.designs });
      }
      applyLogosToCanvas(rec);
      renderLogoPicker(rec);
      renderPrintInfo(rec);
      renderEmbroideryWarnings(rec);
    });
    refreshSummary();
    updateClearButton();
  });

  /* ---- Clear all — wipes apparel and the uploaded logo library ---- */
  function updateClearButton() {
    if (!clearAllBtn) return;
    var hasContent = window.DesignerSession.list().length > 0 ||
      window.DesignerSession.getPageLogos().length > 0;
    clearAllBtn.hidden = !hasContent;
  }

  if (clearAllBtn) {
    clearAllBtn.addEventListener('click', function () {
      var hasContent = window.DesignerSession.list().length > 0 ||
        window.DesignerSession.getPageLogos().length > 0;
      if (!hasContent) return;
      if (!window.confirm('Clear the designer? This removes all apparel and uploaded logos.')) return;
      window.DesignerSession.clear();
      renderAllEntries();
    });
  }

  /* ---- Saved designs (signed-in customers) ----
     Save pushes a snapshot of the whole session to the App Proxy; Resume
     pulls one back via DesignerSession.replaceAll, whose 'replace' event
     triggers a single full re-render here.
  */
  var loggedIn = rootEl.getAttribute('data-logged-in') === 'true';
  var loginUrl = rootEl.getAttribute('data-login-url') || '/account/login';
  var saveBtn = document.querySelector('[data-design-save]');
  var openPanelBtn = document.querySelector('[data-design-open-panel]');
  var savesModal = document.querySelector('[data-design-saves-modal]');
  var savesList = document.querySelector('[data-design-saves-list]');
  var savesEmpty = document.querySelector('[data-design-saves-empty]');
  var savesStatus = document.querySelector('[data-design-saves-status]');
  var currentDesignId = null;

  window.DesignerSession.on('replace', function () { renderAllEntries(); });

  function boardHasContent() {
    return window.DesignerSession.list().length > 0 ||
      window.DesignerSession.getPageLogos().length > 0;
  }

  // Returns true (and redirects to login) if the error is an auth failure.
  // Carries the current page as return_url so login bounces the customer back
  // here afterward; the in-progress board is restored from localStorage.
  function isAuthError(err) {
    if (err && err.message === 'AUTH') {
      var here = window.location.pathname + window.location.search;
      window.location.href = loginUrl +
        (loginUrl.indexOf('?') >= 0 ? '&' : '?') +
        'return_url=' + encodeURIComponent(here);
      return true;
    }
    return false;
  }

  function saveDesign() {
    if (!window.ArtemisDesigns || !saveBtn) return;
    if (!boardHasContent()) {
      summaryStatus.textContent = 'Add apparel or a logo before saving.';
      return;
    }
    var board = window.DesignerSession.readAll();
    var done = function (msg) { saveBtn.disabled = false; summaryStatus.textContent = msg; };

    // A loaded design can be updated in place; otherwise save under a new name.
    if (currentDesignId &&
        window.confirm('Update your loaded design? Click Cancel to save it as a new design instead.')) {
      saveBtn.disabled = true;
      summaryStatus.textContent = 'Saving…';
      window.ArtemisDesigns.update(currentDesignId, { board: board })
        .then(function () { done('Design updated.'); })
        .catch(function (err) { if (!isAuthError(err)) done(err.message || 'Could not save.'); });
      return;
    }
    var name = window.prompt('Name this design:', '');
    if (name == null) return;
    name = name.trim();
    if (!name) { summaryStatus.textContent = 'A name is required to save.'; return; }
    saveBtn.disabled = true;
    summaryStatus.textContent = 'Saving…';
    window.ArtemisDesigns.save(name, board)
      .then(function (saved) { currentDesignId = saved.id; done('Saved as "' + name + '".'); })
      .catch(function (err) { if (!isAuthError(err)) done(err.message || 'Could not save.'); });
  }

  // Focus management for the saved-designs dialog (WCAG 2.4.3).
  var panelEl = savesModal ? savesModal.querySelector('[role="dialog"]') : null;
  var panelLastFocus = null;

  function panelFocusables() {
    if (!panelEl) return [];
    return Array.prototype.filter.call(
      panelEl.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
      function (el) { return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement; }
    );
  }

  function onPanelKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closePanel();
      return;
    }
    if (e.key !== 'Tab') return;
    var focusables = panelFocusables();
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function closePanel() {
    if (!savesModal) return;
    savesModal.hidden = true;
    document.removeEventListener('keydown', onPanelKeydown, true);
    // Return focus to the control that opened the dialog.
    var returnTo = panelLastFocus || openPanelBtn;
    if (returnTo && typeof returnTo.focus === 'function') returnTo.focus();
    panelLastFocus = null;
  }

  function loadDesign(design) {
    if (!window.ArtemisDesigns) return;
    if (boardHasContent() &&
        !window.confirm('Load "' + design.name + '"? This replaces your current board.')) {
      return;
    }
    savesStatus.textContent = 'Loading…';
    window.ArtemisDesigns.get(design.id)
      .then(function (full) {
        window.DesignerSession.replaceAll(full.board); // → 'replace' → renderAllEntries
        currentDesignId = full.id;
        closePanel();
      })
      .catch(function (err) {
        if (!isAuthError(err)) savesStatus.textContent = err.message || 'Could not load that design.';
      });
  }

  function deleteDesign(design, liEl) {
    if (!window.ArtemisDesigns) return;
    if (!window.confirm('Delete "' + design.name + '"? This cannot be undone.')) return;
    window.ArtemisDesigns.remove(design.id)
      .then(function () {
        if (liEl && liEl.parentNode) liEl.parentNode.removeChild(liEl);
        if (currentDesignId === design.id) currentDesignId = null;
        if (savesEmpty && savesList && !savesList.children.length) savesEmpty.hidden = false;
      })
      .catch(function (err) {
        if (!isAuthError(err)) savesStatus.textContent = err.message || 'Could not delete that design.';
      });
  }

  function openPanel() {
    if (!window.ArtemisDesigns || !savesModal) return;
    panelLastFocus = document.activeElement;
    savesModal.hidden = false;
    document.addEventListener('keydown', onPanelKeydown, true);
    // Move focus into the dialog — the close button is the first stable target.
    var closeBtn = savesModal.querySelector('[data-design-saves-close].design-saves__close')
      || savesModal.querySelector('.design-saves__close')
      || (panelFocusables()[0]);
    if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
    savesStatus.textContent = 'Loading…';
    savesList.innerHTML = '';
    if (savesEmpty) savesEmpty.hidden = true;
    window.ArtemisDesigns.list()
      .then(function (designs) {
        savesStatus.textContent = '';
        if (!designs.length) {
          if (savesEmpty) savesEmpty.hidden = false;
          return;
        }
        designs.forEach(function (d) {
          savesList.appendChild(window.ArtemisDesigns.renderRow(d, {
            primaryLabel: 'Load',
            onPrimary: loadDesign,
            onDelete: deleteDesign,
          }));
        });
      })
      .catch(function (err) {
        if (!isAuthError(err)) savesStatus.textContent = err.message || 'Could not load your saved designs.';
      });
  }

  if (loggedIn && saveBtn) saveBtn.addEventListener('click', saveDesign);
  if (loggedIn && openPanelBtn) openPanelBtn.addEventListener('click', openPanel);
  if (savesModal) {
    var closeEls = savesModal.querySelectorAll('[data-design-saves-close]');
    for (var ci = 0; ci < closeEls.length; ci++) {
      closeEls[ci].addEventListener('click', closePanel);
    }
  }

  // Resume a design linked from the account page (?design=<id>).
  function resumeFromUrl() {
    if (!window.ArtemisDesigns || !loggedIn) return;
    var id = null;
    try { id = new URLSearchParams(window.location.search).get('design'); } catch (e) { id = null; }
    if (!id) return;
    window.ArtemisDesigns.get(id)
      .then(function (full) {
        window.DesignerSession.replaceAll(full.board);
        currentDesignId = full.id;
      })
      .catch(function () { /* stale/invalid id — keep the current board */ });
  }

  /* >250-variant hydration. APPAREL_CATALOG (and any stored snapshot) caps each
     product's variants at Shopify's 250 storefront limit, so colors past the cap
     are missing from the size grid and can't be quantity-entered. For every
     product currently in the session whose data is capped, pull the complete set
     from the Storefront API (variant-hydrate.js), patch it into both the catalog
     (which getProductData prefers) and the session snapshot, then re-render.
     No-op when nothing is capped or no Storefront token is configured. */
  function hydrateSessionVariants() {
    if (!window.ArtemisVariants || !window.ArtemisVariants.enabled()) return;
    var handles = {};
    window.DesignerSession.list().forEach(function (e) {
      var p = getProductData(e.productHandle, e.productSnapshot);
      if (p && p.variants && p.variants.length >= 250) handles[e.productHandle] = true;
    });
    var list = Object.keys(handles);
    if (!list.length) return;
    Promise.all(list.map(function (h) {
      return window.ArtemisVariants.fetchAll(h).then(function (full) {
        if (!full || !full.length) return;
        var snap = window.ArtemisVariants.toSnapshot(full);
        if (catalogByHandle[h]) catalogByHandle[h].variants = snap;
        window.DesignerSession.list().forEach(function (e) {
          if (e.productHandle === h && e.productSnapshot) e.productSnapshot.variants = snap;
        });
      });
    })).then(function () { renderAllEntries(); });
  }

  // Resume can bring in products not present at first load — hydrate those too.
  window.DesignerSession.on('replace', function () { hydrateSessionVariants(); });

  renderAllEntries();
  hydrateSessionVariants();
  resumeFromUrl();
})();
